<template>
  <div class="dashboard-container">
    <!-- Dashboard page header dropped — TopBar shows "Dashboard"; the page-level hamburger
         items (Build another campaign / Experience library) are now first-class items
         in the left rail (+ New campaign / Experience Library). -->

    <section class="dashboard-section" aria-label="Campaign portfolio">
      <div
        v-if="attachExperienceKey"
        class="attach-experience-banner"
        role="status"
        aria-live="polite"
      >
        <span class="attach-experience-banner-text">
          Attaching experience <strong>{{ attachExperienceName || attachExperienceKey }}</strong> — pick a campaign below to attach it to.
        </span>
        <button
          type="button"
          class="attach-experience-cancel-link"
          @click="clearAttachParam"
        >
          Cancel
        </button>
      </div>

      <div
        v-if="usage?.campaigns"
        class="usage-banner"
        :class="{ 'usage-banner--at-cap': usage.campaigns.limit !== null && usage.campaigns.active >= usage.campaigns.limit }"
      >
        <span class="usage-banner-primary">
          <strong>{{ usage.campaigns.active }}</strong>
          <span class="usage-banner-of">of</span>
          <strong>{{ usage.campaigns.limit ?? '∞' }}</strong>
          active
        </span>
        <span class="usage-banner-meta">
          <template v-if="usage.campaigns.inactive > 0">{{ usage.campaigns.inactive }} inactive</template>
          <template v-if="usage.campaigns.inactive > 0 && usage.campaigns.ended > 0">  ·  </template>
          <template v-if="usage.campaigns.ended > 0">{{ usage.campaigns.ended }} ended</template>
        </span>
        <span
          v-if="usage.campaigns.limit !== null && usage.campaigns.active >= usage.campaigns.limit"
          class="usage-banner-hint"
        >
          Deactivate one to free a slot, or upgrade your plan.
        </span>
      </div>

      <!-- Over-cap compliance banner: shown when plan downgrade leaves user with more actives than their new cap -->
      <div
        v-if="capStatus && capStatus.overBy > 0"
        class="overcap-banner"
        role="alert"
        aria-live="polite"
        aria-label="Account over campaign limit"
      >
        <div class="overcap-banner-header">
          <span class="overcap-banner-title">You're over your campaign limit</span>
          <span class="overcap-banner-body">
            You're using <strong>{{ capStatus.activeCount }} of {{ capStatus.limit }}</strong> campaigns on your plan.
            Please deactivate <strong>{{ capStatus.overBy }}</strong> to bring your account into compliance.
          </span>
        </div>

        <ul class="overcap-candidate-list" role="group" aria-label="Active campaigns">
          <li
            v-for="campaign in activeCampaigns"
            :key="campaign.id"
            class="overcap-candidate"
            :class="{ 'overcap-candidate--selected': capBannerSelection.has(campaign.id), 'overcap-candidate--done': overCapDeactivateResults.find(r => r.id === campaign.id) }"
          >
            <label class="overcap-candidate-label">
              <input
                type="checkbox"
                :value="campaign.id"
                :checked="capBannerSelection.has(campaign.id)"
                :disabled="isDeactivatingOverCap || !!overCapDeactivateResults.find(r => r.id === campaign.id && r.success)"
                @change="toggleCapBannerSelection(campaign.id)"
              />
              <span class="overcap-candidate-name">{{ campaign.name || 'Untitled campaign' }}</span>
              <span class="overcap-candidate-id">{{ campaign.id }}</span>
              <span
                v-if="overCapDeactivateResults.find(r => r.id === campaign.id)"
                class="overcap-candidate-status"
                :class="overCapDeactivateResults.find(r => r.id === campaign.id)?.success ? 'overcap-candidate-status--ok' : 'overcap-candidate-status--err'"
              >
                {{ overCapDeactivateResults.find(r => r.id === campaign.id)?.success ? 'Deactivated' : 'Failed' }}
              </span>
            </label>
          </li>
        </ul>

        <div v-if="overCapErrorToast" class="overcap-error-toast" role="alert">{{ overCapErrorToast }}</div>

        <div class="overcap-banner-actions">
          <button
            type="button"
            class="primary-button primary-button-small"
            :disabled="capBannerSelection.size < capStatus.overBy || isDeactivatingOverCap"
            @click="applyOverCapDeactivate"
          >
            {{ isDeactivatingOverCap ? 'Deactivating...' : `Deactivate selected (${capBannerSelection.size})` }}
          </button>
        </div>
      </div>

      <div class="filter-toolbar" aria-label="Campaign status filters">
        <div class="filter-row" role="tablist" aria-label="Campaign status filters">
          <button
            v-for="filter in filters"
            :key="filter"
            type="button"
            class="filter-chip"
            :data-filter="filter"
            :class="{ active: selectedFilter === filter }"
            @click="selectedFilter = filter"
          >
            <span class="filter-chip-text">{{ filter }}</span>
            <span class="filter-chip-count">{{ filterCounts[filter] }}</span>
          </button>
        </div>
        <button
          type="button"
          class="refresh-icon-button"
          :class="{ spinning: isLoading }"
          :disabled="isLoading"
          aria-label="Refresh campaigns"
          @click="loadCampaigns"
        >
          <span class="refresh-icon" aria-hidden="true"></span>
          <span class="refresh-button-text">Refresh</span>
        </button>
      </div>

      <div v-if="loadError" class="error-banner">{{ loadError }}</div>
      <div v-else-if="analyticsWarning" class="info-banner">{{ analyticsWarning }}</div>
      <div v-else-if="isLoading" class="empty-state">Loading campaigns...</div>
      <div v-else-if="visibleCampaigns.length === 0" class="empty-state">No campaigns found for this filter.</div>

      <div v-else class="campaign-list" role="table" aria-label="Campaign list">
        <div class="campaign-list-header" role="row">
          <span class="campaign-list-heading campaign-list-heading-name" role="columnheader">Campaign</span>
          <span class="campaign-list-heading campaign-list-heading-status" role="columnheader">Status</span>
          <span class="campaign-list-heading" role="columnheader">Schedule</span>
          <span class="campaign-list-heading" role="columnheader">Tallies</span>
          <span class="campaign-list-heading campaign-list-heading-actions" role="columnheader">Actions</span>
        </div>

        <article
          v-for="campaign in visibleCampaigns"
          :key="campaign.id"
          :ref="(el) => setGameplaysRowRef(el, campaign)"
          class="campaign-row"
          role="row"
        >
          <div class="campaign-primary">
            <div class="campaign-title-row">
              <h4 class="campaign-title">{{ campaign.name || 'Untitled campaign' }}</h4>
            </div>
            <div class="campaign-meta-row">
              <span class="campaign-meta-text">Created {{ formatDate(campaign.createdAt) }}</span>
              <span class="campaign-meta-separator">•</span>
              <span class="campaign-meta-text">{{ campaign.id }}</span>
            </div>
            <div class="campaign-meta-row">
              <span class="campaign-meta-label">Platform</span>
              <span class="campaign-meta-text">{{ campaign.status || 'Not set' }}</span>
            </div>
          </div>

          <div class="campaign-status-column">
            <span class="status-badge" :class="statusClassMap[campaign.lifecycleStatus]">
              {{ campaign.lifecycleStatus }}
            </span>
          </div>

          <div class="campaign-schedule">
            <div class="schedule-line">
              <span class="schedule-label">Starts</span>
              <span class="schedule-value">{{ formatDateTime(campaign.startDate) }}</span>
            </div>
            <div class="schedule-line">
              <span class="schedule-label">Ends</span>
              <span class="schedule-value">{{ formatDateTime(campaign.endDate) }}</span>
            </div>
          </div>

          <div class="metrics-inline">
            <div class="metric-inline-item">
              <span class="metric-inline-label">Registered</span>
              <span class="metric-inline-value">{{ formatNumber(campaign.metrics.registered) }}</span>
            </div>
            <div class="metric-inline-item">
              <span class="metric-inline-label">Gameplays</span>
              <span class="metric-inline-value">{{ gameplaysDisplay(campaign.id) }}</span>
            </div>
          </div>

          <div class="row-actions">
            <button
              v-if="attachExperienceKey"
              type="button"
              class="attach-cta-button"
              @click="openAttachConfirm(campaign)"
            >
              Attach experience here
            </button>
            <div
              class="row-menu"
              :class="{
                open: openMenuCampaignId === campaign.id,
                'row-menu--drop-up': openMenuCampaignId === campaign.id && openMenuDirection === 'up',
              }"
            >
              <button
                type="button"
                class="hamburger"
                :class="{ 'menu-open': openMenuCampaignId === campaign.id }"
                :aria-expanded="openMenuCampaignId === campaign.id ? 'true' : 'false'"
                aria-label="Campaign actions"
                @click.stop="toggleCampaignMenu(campaign.id, $event)"
              >
                <span></span>
                <span></span>
                <span></span>
              </button>

              <div
                v-if="openMenuCampaignId === campaign.id"
                class="row-menu-panel"
                role="menu"
              >
                <button
                  type="button"
                  class="row-menu-item"
                  role="menuitem"
                  @click="openEditFromMenu(campaign)"
                >
                  Edit
                </button>
                <button
                  type="button"
                  class="row-menu-item"
                  role="menuitem"
                  :disabled="pendingCampaignId === campaign.id"
                  @click="togglePausedFromMenu(campaign)"
                >
                  {{ campaign.paused ? 'Activate' : 'Deactivate' }}
                </button>
                <button
                  v-if="supportsResults(campaign)"
                  type="button"
                  class="row-menu-item"
                  role="menuitem"
                  @click="openResponsesFromMenu(campaign)"
                >
                  View results
                </button>
                <a
                  class="row-menu-item"
                  role="menuitem"
                  :href="campaign.openCampaignUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click="closeCampaignMenu"
                >
                  Open campaign
                </a>
                <button
                  type="button"
                  class="row-menu-item"
                  role="menuitem"
                  @click="copyShareLinkFromMenu(campaign)"
                >
                  {{ copiedCampaignId === campaign.id ? 'Copied' : 'Copy link' }}
                </button>
                <button
                  type="button"
                  class="row-menu-item"
                  role="menuitem"
                  @click="openStandardAnalytics(campaign)"
                >
                  <span class="row-menu-beta-tag">Beta</span>Analytics
                </button>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>

    <p v-if="lastUpdatedLabel" class="dashboard-updated-at">{{ lastUpdatedLabel }}</p>

    <Teleport to="body">
    <div
      v-if="isEditModalOpen"
      class="modal-overlay"
      @click.self="closeEditModal"
    >
      <div class="modal-card modal-card--extended" role="dialog" aria-modal="true" aria-labelledby="editCampaignTitle">
        <h3 id="editCampaignTitle">Edit campaign</h3>
        <p class="modal-subtitle">Update basic campaign metadata for the selected campaign.</p>

        <div class="modal-grid">
          <label class="modal-field" for="edit-campaign-name">
            Campaign name
            <input
              id="edit-campaign-name"
              v-model="editCampaignName"
              class="modal-input"
              type="text"
              maxlength="120"
            >
          </label>

          <label class="modal-field" for="edit-timezone">
            Timezone
            <select
              id="edit-timezone"
              v-model="editTimezone"
              class="modal-input"
            >
              <option
                v-for="timezone in timezoneOptions"
                :key="timezone"
                :value="timezone"
              >
                {{ timezone }}
              </option>
            </select>
          </label>

          <div class="modal-date-row">
            <label class="modal-field" for="edit-start-date">
              Start date
              <input
                id="edit-start-date"
                v-model="editStartDate"
                class="modal-input"
                type="date"
              >
            </label>

            <label class="modal-field" for="edit-end-date">
              End date
              <input
                id="edit-end-date"
                v-model="editEndDate"
                class="modal-input"
                type="date"
              >
            </label>
          </div>
        </div>

        <p v-if="editError" class="modal-error">{{ editError }}</p>

        <div class="modal-actions modal-actions--equal">
          <button
            type="button"
            class="primary-button primary-button-small"
            @click="closeEditModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="primary-button primary-button-small"
            :disabled="isSavingEdit"
            @click="saveEditedCampaign"
          >
            {{ isSavingEdit ? 'Saving...' : 'Save changes' }}
          </button>
        </div>

        <!-- ─── Section 1: Voucher Pool ─────────────────────────────────── -->
        <div class="pool-section">
          <div class="pool-section-header">
            <h4 class="pool-section-title">Voucher Pool</h4>
          </div>

          <!-- Prize selector — shown only when campaign has multiple prizes -->
          <div v-if="poolState.prizes.length > 1" class="pool-selector-row">
            <label class="pool-field-label" for="pool-prize-select">Prize</label>
            <select
              id="pool-prize-select"
              v-model="poolState.selectedPrizeId"
              class="modal-input pool-select"
              @change="onPrizeSelected"
            >
              <option
                v-for="prize in poolState.prizes"
                :key="prize.id"
                :value="prize.id"
              >
                {{ prize.name || prize.id }}
              </option>
            </select>
          </div>

          <!-- Loading / error states for pool status -->
          <p v-if="poolState.statusError" class="pool-section-error">{{ poolState.statusError }}</p>

          <!-- Live counter strip -->
          <div v-if="poolState.selectedPrizeId" class="pool-counter-strip">
            <span class="pool-counter-item">
              <span class="pool-counter-label">Total</span>
              <span class="pool-counter-value">{{ poolState.statusLoading ? '…' : poolState.status?.total ?? '—' }}</span>
            </span>
            <span class="pool-counter-sep" aria-hidden="true">·</span>
            <span class="pool-counter-item">
              <span class="pool-counter-label">Remaining</span>
              <span class="pool-counter-value">{{ poolState.statusLoading ? '…' : poolState.status?.remaining ?? '—' }}</span>
            </span>
            <span class="pool-counter-sep" aria-hidden="true">·</span>
            <span class="pool-counter-item">
              <span class="pool-counter-label">Claimed</span>
              <span class="pool-counter-value">{{ poolState.statusLoading ? '…' : (poolState.status?.claimed ?? 'TBD') }}</span>
            </span>
            <span class="pool-counter-sep" aria-hidden="true">·</span>
            <span class="pool-counter-item">
              <span class="pool-counter-label">Redeemed</span>
              <span class="pool-counter-value">{{ poolState.statusLoading ? '…' : (poolState.status?.redeemed ?? 'TBD') }}</span>
            </span>
            <span class="pool-counter-sep" aria-hidden="true">·</span>
            <span class="pool-counter-item">
              <span class="pool-counter-label">Allotted to pool</span>
              <span class="pool-counter-value">{{ poolState.statusLoading ? '…' : (poolState.status?.allotment ?? 'all') }}</span>
            </span>
          </div>

          <!-- Empty state when no prize resolved yet -->
          <p v-else-if="!poolState.statusLoading && poolState.prizes.length === 0" class="pool-empty-note">
            No prizes found for this campaign.
          </p>

          <!-- Add codes panel — tabs: Upload CSV / Generate -->
          <div v-if="poolState.selectedPrizeId" class="pool-add-panel">
            <div class="pool-add-tabs" role="tablist" aria-label="Add codes mode">
              <button
                type="button"
                role="tab"
                :aria-selected="poolState.addMode === 'upload'"
                :class="['pool-tab', { 'pool-tab--active': poolState.addMode === 'upload' }]"
                @click="triggerCsvUpload"
              >
                Upload CSV
              </button>
              <button
                type="button"
                role="tab"
                :aria-selected="poolState.addMode === 'generate'"
                :class="['pool-tab', { 'pool-tab--active': poolState.addMode === 'generate' }]"
                @click="poolState.addMode = 'generate'"
              >
                Generate
              </button>
            </div>

            <!-- Hidden file input — opened by the Upload CSV button above -->
            <input
              id="pool-csv-upload"
              ref="csvFileInput"
              type="file"
              accept=".csv,text/csv"
              class="pool-file-input"
              @change="onCsvFileSelected"
            >

            <!-- Upload CSV tab -->
            <div
              v-if="poolState.addMode === 'upload'"
              role="tabpanel"
              class="pool-add-body"
            >
              <p v-if="poolState.csvFileName" class="pool-csv-selected">
                Selected: <strong>{{ poolState.csvFileName }}</strong>
                <span v-if="poolState.csvPreviewCount !== null"> — {{ poolState.csvPreviewCount }} codes parsed</span>
              </p>
              <p v-else class="pool-csv-preview">
                Click “Upload CSV” above to choose a .csv file — one voucher code per line.
              </p>
            </div>

            <!-- Generate tab -->
            <div
              v-if="poolState.addMode === 'generate'"
              role="tabpanel"
              class="pool-add-body pool-generate-form"
            >
              <label class="pool-field-label" for="pool-gen-count">
                Quantity
                <input
                  id="pool-gen-count"
                  v-model.number="poolState.generateCount"
                  class="modal-input"
                  type="number"
                  min="1"
                  max="10000"
                >
              </label>
              <label class="pool-field-label" for="pool-gen-length">
                Code length
                <input
                  id="pool-gen-length"
                  v-model.number="poolState.generateLength"
                  class="modal-input"
                  type="number"
                  min="4"
                  max="32"
                >
              </label>
              <label class="pool-field-label" for="pool-gen-prefix">
                Prefix <span class="pool-field-optional">(optional)</span>
                <input
                  id="pool-gen-prefix"
                  v-model="poolState.generatePrefix"
                  class="modal-input"
                  type="text"
                  maxlength="20"
                  placeholder="e.g. PROMO-"
                >
              </label>
            </div>

            <p v-if="poolState.addError" class="pool-section-error">{{ poolState.addError }}</p>
            <p v-if="poolState.addToast" class="pool-toast">{{ poolState.addToast }}</p>
          </div>

          <!-- Actions: submit (add/generate) + remove unclaimed, same row -->
          <div v-if="poolState.selectedPrizeId">
            <div class="pool-actions-row">
              <!-- Submit: Add codes (upload mode) / Generate codes (generate mode) -->
              <button
                v-if="poolState.addMode === 'upload'"
                type="button"
                class="primary-button primary-button-small pool-action-btn"
                :disabled="!poolState.csvCodes.length || poolState.addLoading"
                @click="submitAddCodes"
              >
                {{ poolState.addLoading ? 'Adding…' : 'Add codes' }}
              </button>
              <button
                v-else
                type="button"
                class="primary-button primary-button-small pool-action-btn"
                :disabled="!poolState.generateCount || !poolState.generateLength || poolState.addLoading"
                @click="submitGenerateCodes"
              >
                {{ poolState.addLoading ? 'Generating…' : 'Generate codes' }}
              </button>

              <!-- Remove unclaimed -->
              <button
                v-if="!poolState.removeUnclaimedConfirm"
                type="button"
                class="primary-button primary-button-small pool-action-btn pool-action-btn--danger"
                :disabled="poolState.removeUnclaimedLoading"
                @click="poolState.removeUnclaimedConfirm = true"
              >
                Remove all unclaimed codes
              </button>
              <div v-else class="pool-confirm-inline">
                <span class="pool-confirm-label">Remove all unclaimed codes — are you sure?</span>
                <button
                  type="button"
                  class="pool-danger-confirm"
                  :disabled="poolState.removeUnclaimedLoading"
                  @click="confirmRemoveUnclaimed"
                >
                  {{ poolState.removeUnclaimedLoading ? 'Removing…' : 'Yes, remove' }}
                </button>
                <button
                  type="button"
                  class="action-link action-link-button"
                  :disabled="poolState.removeUnclaimedLoading"
                  @click="poolState.removeUnclaimedConfirm = false"
                >
                  Cancel
                </button>
              </div>
            </div>
            <p v-if="poolState.removeUnclaimedError" class="pool-section-error">{{ poolState.removeUnclaimedError }}</p>
            <p v-if="poolState.removeUnclaimedToast" class="pool-toast">{{ poolState.removeUnclaimedToast }}</p>
          </div>

          <!-- Accordion: list of codes with per-code remove -->
          <div v-if="poolState.selectedPrizeId" class="pool-codes-accordion">
            <button
              type="button"
              class="pool-codes-toggle"
              :aria-expanded="poolState.codesOpen"
              @click="toggleCodesAccordion"
            >
              <span>{{ poolState.codesOpen ? 'Hide' : 'Show' }} code list</span>
              <span class="pool-codes-toggle-chevron" :class="{ 'pool-codes-toggle-chevron--open': poolState.codesOpen }">▾</span>
            </button>
            <div v-if="poolState.codesOpen" class="pool-codes-list">
              <p v-if="poolState.codesLoading" class="pool-codes-loading">Loading…</p>
              <p v-else-if="poolState.codesError" class="pool-section-error">{{ poolState.codesError }}</p>
              <template v-else>
                <div
                  v-for="item in poolState.codesPage"
                  :key="item.code"
                  :class="['pool-code-row', { 'pool-code-row--claimed': item.claimed }]"
                >
                  <span class="pool-code-value">{{ item.code }}</span>
                  <span v-if="item.claimed" class="pool-code-claimed-badge" title="Claimed — cannot remove">🔒</span>
                  <button
                    v-else
                    type="button"
                    class="pool-code-remove"
                    :disabled="poolState.removingCode === item.code"
                    :title="`Remove ${item.code}`"
                    @click="removeCode(item.code)"
                  >
                    {{ poolState.removingCode === item.code ? '…' : '✕' }}
                  </button>
                </div>
                <div v-if="poolState.codesPage.length === 0" class="pool-codes-empty">No codes found.</div>
                <!-- Pagination -->
                <div v-if="poolState.codesTotalPages > 1" class="pool-codes-pagination">
                  <button
                    type="button"
                    class="action-link action-link-button"
                    :disabled="poolState.codesCurrentPage <= 1"
                    @click="loadCodesPage(poolState.codesCurrentPage - 1)"
                  >
                    ‹ Prev
                  </button>
                  <span class="pool-codes-page-info">{{ poolState.codesCurrentPage }} / {{ poolState.codesTotalPages }}</span>
                  <button
                    type="button"
                    class="action-link action-link-button"
                    :disabled="poolState.codesCurrentPage >= poolState.codesTotalPages"
                    @click="loadCodesPage(poolState.codesCurrentPage + 1)"
                  >
                    Next ›
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- ─── Section 2: Pool Allotment ────────────────────────────────── -->
        <div class="pool-section">
          <div class="pool-section-header">
            <h4 class="pool-section-title">Pool Allotment</h4>
          </div>

          <!-- Drop pool selector — shown only when campaign has multiple drop pools -->
          <div v-if="poolState.dropPools.length > 1" class="pool-selector-row">
            <label class="pool-field-label" for="pool-droppoolselect">Drop pool</label>
            <select
              id="pool-droppoolselect"
              v-model="poolState.selectedDropPoolId"
              class="modal-input pool-select"
            >
              <option
                v-for="dp in poolState.dropPools"
                :key="dp.id"
                :value="dp.id"
              >
                {{ dp.name || dp.id }}
              </option>
            </select>
          </div>

          <template v-if="poolState.selectedDropPoolId || poolState.dropPools.length === 0">
            <p class="pool-allotment-description">
              How many of the {{ poolState.status?.total ?? '?' }} prizes can the drop pool hand out?
              Default: all of them. Lower this to hold prizes back.
            </p>

            <div class="pool-allotment-control">
              <input
                id="pool-allotment-input"
                v-model.number="poolState.allotmentDraft"
                class="modal-input pool-allotment-input"
                type="number"
                min="0"
                :max="poolState.status?.total ?? undefined"
                :disabled="poolState.allotmentLoading || !poolState.status"
              >
              <p class="pool-allotment-preview">
                <template v-if="poolState.allotmentDraft !== null && poolState.status">
                  {{ poolState.allotmentDraft }} of {{ poolState.status.total }} prizes available for distribution
                </template>
                <template v-else>
                  Enter a value above to set the allotment.
                </template>
              </p>
            </div>

            <p v-if="poolState.allotmentError" class="pool-section-error">{{ poolState.allotmentError }}</p>
            <p v-if="poolState.allotmentToast" class="pool-toast">{{ poolState.allotmentToast }}</p>

            <div class="pool-allotment-actions">
              <button
                type="button"
                class="primary-button primary-button-small"
                :disabled="poolState.allotmentLoading || !poolState.selectedDropPoolId || !poolState.selectedPrizeId || poolState.allotmentDraft === null"
                @click="saveAllotment"
              >
                {{ poolState.allotmentLoading ? 'Saving…' : 'Save allotment' }}
              </button>
            </div>
          </template>
          <p v-else-if="poolState.dropPools.length > 0 && !poolState.selectedDropPoolId" class="pool-empty-note">
            Select a drop pool above to configure allotment.
          </p>
        </div>

        <!-- ─── Section 3: Rounds (explicit-rounds schedules only) ─────────── -->
        <div class="pool-section rounds-section">
          <h4 class="pool-section-title">Rounds</h4>

          <p v-if="roundsState.loading" class="pool-empty-note">Loading rounds…</p>

          <p v-else-if="roundsState.error" class="pool-section-error">{{ roundsState.error }}</p>

          <template v-else-if="roundsState.scheduleType === 'explicit-rounds'">
            <p class="rounds-description">
              Edit the start and end time of each existing round. Rounds may swap order but must not overlap.
              To add or remove rounds, use the Experience Library.
            </p>

            <div v-for="(round, idx) in roundsState.drafts" :key="round.id" class="round-row">
              <div class="round-label">{{ round.name || `Round ${idx + 1}` }}</div>
              <div class="round-fields">
                <label class="round-field">
                  <span>Start</span>
                  <input
                    type="datetime-local"
                    class="modal-input round-input"
                    :value="round.startDateLocal"
                    @input="onRoundDateChange(round.id, 'startDateLocal', ($event.target as HTMLInputElement).value)"
                  >
                </label>
                <label class="round-field">
                  <span>End</span>
                  <input
                    type="datetime-local"
                    class="modal-input round-input"
                    :value="round.endDateLocal"
                    @input="onRoundDateChange(round.id, 'endDateLocal', ($event.target as HTMLInputElement).value)"
                  >
                </label>
                <label class="round-field round-field-attempts">
                  <span>Attempts / user</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    placeholder="∞"
                    class="modal-input round-input round-input-attempts"
                    :value="round.maxAttemptsDraft ?? ''"
                    @input="onRoundMaxAttemptsChange(round.id, ($event.target as HTMLInputElement).value)"
                  >
                </label>
              </div>
            </div>

            <p v-if="roundsValidationError" class="pool-section-error">{{ roundsValidationError }}</p>
            <p v-if="roundsState.toast" class="pool-toast">{{ roundsState.toast }}</p>

            <div class="rounds-actions">
              <button
                type="button"
                class="primary-button primary-button-small"
                :disabled="roundsState.saving || !!roundsValidationError"
                @click="saveRounds"
              >
                {{ roundsState.saving ? 'Saving…' : 'Save rounds' }}
              </button>
              <a
                class="rounds-library-link"
                href="/dashboard/experiences"
                target="_blank"
                rel="noopener"
              >
                Open Experience Library ↗
              </a>
            </div>
          </template>

          <template v-else-if="roundsState.scheduleType === 'periodic'">
            <p class="pool-empty-note">
              This campaign uses a periodic schedule (uniform interval), not explicit rounds.
              Edit it in the Experience Library.
            </p>
            <div class="rounds-actions">
              <a
                class="rounds-library-link"
                href="/dashboard/experiences"
                target="_blank"
                rel="noopener"
              >
                Open Experience Library ↗
              </a>
            </div>
          </template>

          <template v-else>
            <p class="pool-empty-note">No round schedule configured for this campaign.</p>
          </template>
        </div>

        <!-- ─── Section 4: Survey / Quiz Responses ───────────────────────── -->
        <div v-if="editingCampaign && supportsResults(editingCampaign)" class="pool-section">
          <div class="pool-section-header">
            <h4 class="pool-section-title">Responses</h4>
          </div>
          <SurveyResponsesPanel
            :campaign-id="editingCampaign.id"
            :campaign-name="editingCampaign.name"
          />
        </div>

        <!-- ─── Section 5: Prize Distribution ─────────────────────────── -->
        <div
          v-if="roundsState.scheduleType === 'explicit-rounds' && roundsState.drafts.length > 0"
          class="pool-section"
        >
          <div class="pool-section-header">
            <h4 class="pool-section-title">Prize Distribution</h4>
          </div>
          <div
            v-for="(round, idx) in roundsState.drafts"
            :key="round.id"
            class="prize-dist-round"
          >
            <p class="prize-dist-round-label">{{ round.name || `Round ${idx + 1}` }}</p>
            <PrizeDistributionPanel
              :campaign-id="editingCampaign?.id || ''"
              :round-id="round.id"
              :round-index="idx"
              :round-start-date="round.startDateLocal"
              :round-end-date="round.endDateLocal"
              :prize-distribution="round.prizeDistribution ?? null"
              :saving="prizeDist.saving"
              :save-error="prizeDist.errorByRound[round.id] || ''"
              :save-toast="prizeDist.toastByRound[round.id] || ''"
              @save="(dist) => savePrizeDistribution(round.id, dist)"
            />
          </div>
        </div>

      </div>
    </div>
    </Teleport>

    <SurveyResponsesModal
      :visible="responsesModal.visible"
      :campaign-id="responsesModal.campaignId"
      :campaign-name="responsesModal.campaignName"
      @close="closeResponsesModal"
    />

    <teleport to="body">
      <div v-if="swapModal.open" class="swap-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="swap-modal-title">
        <div class="swap-modal">
          <h3 id="swap-modal-title" class="swap-modal-title">At your active-campaign limit</h3>
          <p class="swap-modal-body">
            You're already running {{ swapModal.activeCount }} of {{ swapModal.limit }} campaigns.
            Pick one to deactivate and we'll activate
            <strong>{{ swapModal.targetName }}</strong> in its place.
          </p>
          <div v-if="swapCandidates.length === 0" class="swap-modal-empty">
            No other active campaigns available to deactivate.
          </div>
          <ul v-else class="swap-candidate-list" role="radiogroup">
            <li v-for="candidate in swapCandidates" :key="candidate.id">
              <label class="swap-candidate">
                <input
                  type="radio"
                  name="swap-candidate"
                  :value="candidate.id"
                  v-model="swapSelection"
                />
                <span class="swap-candidate-name">{{ candidate.name || 'Untitled campaign' }}</span>
                <span class="swap-candidate-id">{{ candidate.id }}</span>
              </label>
            </li>
          </ul>
          <div class="swap-modal-actions">
            <button
              type="button"
              class="secondary-button secondary-button-small"
              :disabled="isSwapping"
              @click="closeSwapModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="primary-button primary-button-small"
              :disabled="!swapSelection || isSwapping"
              @click="applySwap"
            >
              {{ isSwapping ? 'Swapping...' : 'Swap' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <teleport to="body">
      <div
        v-if="attachConfirm.open"
        class="modal-overlay"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="'attach-confirm-title'"
        @click.self="closeAttachConfirm"
        @keydown.esc="closeAttachConfirm"
      >
        <div class="modal-card" tabindex="-1" ref="attachConfirmCardRef">
          <h3 id="attach-confirm-title">Attach experience to campaign?</h3>
          <p class="modal-subtitle">
            Attach <strong>{{ attachConfirm.experienceName }}</strong> to
            <strong>{{ attachConfirm.campaignName }}</strong>?
            This replaces any existing attached experience on this campaign.
          </p>
          <p v-if="attachConfirm.error" class="modal-error">{{ attachConfirm.error }}</p>
          <div class="modal-actions">
            <button
              type="button"
              class="action-link action-link-button"
              :disabled="attachConfirm.saving"
              @click="closeAttachConfirm"
            >
              Cancel
            </button>
            <button
              type="button"
              class="primary-button primary-button-small"
              :disabled="attachConfirm.saving"
              @click="confirmAttach"
            >
              {{ attachConfirm.saving ? 'Attaching...' : 'Attach experience' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { campaignsService, type ManagedCampaign, type CapStatus } from '@/services/campaigns'
import { analyticsService } from '@/services/analytics'
import { billingService, type OrgUsageSnapshot } from '@/services/billing'
import {
  prizesService,
  type PoolStatus,
  type PrizeSummary,
  type DropPoolSummary,
  type VoucherCode,
} from '@/services/prizes'
import { roundsService, type RoundDef, type PrizeDistribution } from '@/services/rounds'
import SurveyResponsesModal from '@/components/SurveyResponsesModal.vue'
import SurveyResponsesPanel from '@/components/SurveyResponsesPanel.vue'
import PrizeDistributionPanel from '@/components/PrizeDistributionPanel.vue'
import { useAuthStore } from '@/store/auth.store'
import { useCampaignBuilderStore } from '@/store/campaignBuilder.store'
import { useUIStore } from '@/store/ui.store'
import { useFormStore } from '@/store/form.store'
import { getExperienceLibraryRecord, type ExperienceLibraryRecord } from '@/utils/experienceLibrary'
import type { AttachedExperienceReference } from '@/types/form.types'

type CampaignFilter = 'All' | 'Active' | 'Scheduled' | 'Ended' | 'Paused' | 'Unscheduled'

type DashboardMetrics = {
  registered: number
  claimed: number
  redeemed: number
  shared: number
}

type DashboardCampaign = ManagedCampaign & {
  lifecycleStatus: Exclude<CampaignFilter, 'All'>
  accessUrl: string
  openCampaignUrl: string
  shareUrl: string
  metrics: DashboardMetrics
}

const CAMPAIGN_BUILDER_STATE_KEY = 'campaignBuilderState'
const UNSCHEDULED_LABEL = 'Not scheduled'
const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
}
const DATE_TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
}
const filters: CampaignFilter[] = ['All', 'Active', 'Scheduled', 'Paused', 'Ended']

const statusClassMap: Record<Exclude<CampaignFilter, 'All'>, string> = {
  Active: 'active',
  Scheduled: 'scheduled',
  Paused: 'paused',
  Ended: 'ended',
  Unscheduled: 'unscheduled',
}

const EMPTY_METRICS: DashboardMetrics = {
  registered: 0,
  claimed: 0,
  redeemed: 0,
  shared: 0,
}

const FALLBACK_TIMEZONES = [
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
]

const padTwo = (value: number): string => String(value).padStart(2, '0')

const toDateInputValue = (value?: string | null): string => {
  if (!value) {
    return ''
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`
}

const fromDateInputToIso = (value: string): string | null => {
  if (!value) {
    return null
  }
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) {
    return null
  }
  const date = new Date(year, month - 1, day, 9, 0, 0, 0)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return date.toISOString()
}

const deriveLifecycleStatus = (campaign: ManagedCampaign): Exclude<CampaignFilter, 'All'> => {
  if (campaign.paused) {
    return 'Paused'
  }

  const now = Date.now()
  const start = campaign.startDate ? new Date(campaign.startDate).getTime() : null
  const end = campaign.endDate ? new Date(campaign.endDate).getTime() : null

  if (!start && !end) {
    return 'Unscheduled'
  }
  if (start && !Number.isNaN(start) && now < start) {
    return 'Scheduled'
  }
  if (end && !Number.isNaN(end) && now > end) {
    return 'Ended'
  }
  return 'Active'
}

const toDashboardCampaign = (campaign: ManagedCampaign): DashboardCampaign => {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const liveAppOrigin = origin.includes('builder.')
    ? origin.replace('builder.', 'app.')
    : origin.includes('creator.innereality.com')
      ? 'https://app.prodc.innereality.com'
      : origin
  const accessUrl = `${origin}/entry?campaign=${encodeURIComponent(campaign.id)}`
  const openCampaignUrl = `${liveAppOrigin}/${encodeURIComponent(campaign.id)}`
  const shareUrl = `${origin}/entry?campaign=${encodeURIComponent(campaign.id)}&source=share`

  return {
    ...campaign,
    lifecycleStatus: deriveLifecycleStatus(campaign),
    accessUrl,
    openCampaignUrl,
    shareUrl,
    metrics: { ...EMPTY_METRICS },
  }
}

const copyToClipboard = async (value: string): Promise<boolean> => {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch (error) {
    console.warn('Clipboard API unavailable:', error)
  }

  try {
    const input = document.createElement('input')
    input.value = value
    input.setAttribute('readonly', '')
    input.style.position = 'absolute'
    input.style.left = '-9999px'
    document.body.appendChild(input)
    input.select()
    const copied = document.execCommand('copy')
    document.body.removeChild(input)
    return copied
  } catch (error) {
    console.warn('Fallback copy failed:', error)
    return false
  }
}

export default defineComponent({
  name: 'DashboardView',
  components: { SurveyResponsesModal, SurveyResponsesPanel, PrizeDistributionPanel },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const authStore = useAuthStore()
    const campaignBuilderStore = useCampaignBuilderStore()
    const uiStore = useUIStore()
    const formStore = useFormStore()

    const campaigns = ref<DashboardCampaign[]>([])
    const copiedCampaignId = ref<string | null>(null)
    const isEditModalOpen = ref(false)
    const editingCampaignId = ref<string | null>(null)
    const editCampaignName = ref('')
    const editStartDate = ref('')
    const editEndDate = ref('')
    const editTimezone = ref('')
    const editError = ref('')
    const isLoading = ref(false)
    const isSavingEdit = ref(false)
    const pendingCampaignId = ref<string | null>(null)
    const loadError = ref('')
    const analyticsWarning = ref('')
    const openMenuCampaignId = ref<string | null>(null)
    const openMenuDirection = ref<'up' | 'down'>('down')
    const isDashboardMenuOpen = ref(false)
    const selectedFilter = ref<CampaignFilter>('All')
    const lastUpdatedAt = ref<number | null>(null)
    const usage = ref<OrgUsageSnapshot | null>(null)
    const swapModal = ref<{
      open: boolean
      targetCampaignId: string | null
      targetName: string
      activeCount: number
      limit: number
    }>({ open: false, targetCampaignId: null, targetName: '', activeCount: 0, limit: 0 })
    const swapSelection = ref<string | null>(null)
    const isSwapping = ref(false)

    // Over-cap banner state — populated on mount and after any deactivate in the banner flow
    const capStatus = ref<CapStatus | null>(null)
    const capBannerSelection = ref<Set<string>>(new Set())
    const isDeactivatingOverCap = ref(false)
    const overCapDeactivateResults = ref<{ id: string; name: string; success: boolean; error?: string }[]>([])
    const overCapErrorToast = ref<string | null>(null)

    const responsesModal = ref<{
      visible: boolean
      campaignId: string | null
      campaignName: string | null
    }>({ visible: false, campaignId: null, campaignName: null })
    let copyResetTimeout: number | null = null

    // ─── Voucher Pool + Pool Allotment state ──────────────────────────────
    //
    // All pool state lives in a single reactive object to keep it clearly
    // separate from the basic campaign edit fields and easy to reset in one go.
    const poolState = reactive<{
      // Prize selector
      prizes: PrizeSummary[]
      selectedPrizeId: string | null
      prizesLoading: boolean

      // Drop pool selector
      dropPools: DropPoolSummary[]
      selectedDropPoolId: string | null

      // Live counter strip
      status: PoolStatus | null
      statusLoading: boolean
      statusError: string | null

      // Add codes — shared
      addMode: 'upload' | 'generate'
      addLoading: boolean
      addError: string | null
      addToast: string | null

      // Add codes — Upload CSV mode
      csvCodes: string[]
      csvFileName: string | null
      csvPreviewCount: number | null

      // Add codes — Generate mode
      generateCount: number
      generateLength: number
      generatePrefix: string

      // Remove unclaimed
      removeUnclaimedConfirm: boolean
      removeUnclaimedLoading: boolean
      removeUnclaimedError: string | null
      removeUnclaimedToast: string | null

      // Code list accordion
      codesOpen: boolean
      codesLoading: boolean
      codesError: string | null
      codesPage: VoucherCode[]
      codesCurrentPage: number
      codesTotalPages: number
      removingCode: string | null

      // Pool allotment
      allotmentDraft: number | null
      allotmentLoading: boolean
      allotmentError: string | null
      allotmentToast: string | null
    }>({
      prizes: [],
      selectedPrizeId: null,
      prizesLoading: false,

      dropPools: [],
      selectedDropPoolId: null,

      status: null,
      statusLoading: false,
      statusError: null,

      addMode: 'upload',
      addLoading: false,
      addError: null,
      addToast: null,

      csvCodes: [],
      csvFileName: null,
      csvPreviewCount: null,

      generateCount: 100,
      generateLength: 8,
      generatePrefix: '',

      removeUnclaimedConfirm: false,
      removeUnclaimedLoading: false,
      removeUnclaimedError: null,
      removeUnclaimedToast: null,

      codesOpen: false,
      codesLoading: false,
      codesError: null,
      codesPage: [],
      codesCurrentPage: 1,
      codesTotalPages: 1,
      removingCode: null,

      allotmentDraft: null,
      allotmentLoading: false,
      allotmentError: null,
      allotmentToast: null,
    })

    const resetPoolState = () => {
      poolState.prizes = []
      poolState.selectedPrizeId = null
      poolState.prizesLoading = false
      poolState.dropPools = []
      poolState.selectedDropPoolId = null
      poolState.status = null
      poolState.statusLoading = false
      poolState.statusError = null
      poolState.addMode = 'upload'
      poolState.addLoading = false
      poolState.addError = null
      poolState.addToast = null
      poolState.csvCodes = []
      poolState.csvFileName = null
      poolState.csvPreviewCount = null
      poolState.generateCount = 100
      poolState.generateLength = 8
      poolState.generatePrefix = ''
      poolState.removeUnclaimedConfirm = false
      poolState.removeUnclaimedLoading = false
      poolState.removeUnclaimedError = null
      poolState.removeUnclaimedToast = null
      poolState.codesOpen = false
      poolState.codesLoading = false
      poolState.codesError = null
      poolState.codesPage = []
      poolState.codesCurrentPage = 1
      poolState.codesTotalPages = 1
      poolState.removingCode = null
      poolState.allotmentDraft = null
      poolState.allotmentLoading = false
      poolState.allotmentError = null
      poolState.allotmentToast = null
    }

    // ─── Rounds (explicit-rounds schedule editing) ───────────────────────────
    // Sibling to poolState. Same modal, separate concern: per-round start/end
    // datetime + optional per-user attempts cap. Loaded lazily on modal open,
    // reset on close. No-op (with notice) for periodic / no-schedule campaigns.

    type RoundDraft = {
      id: string
      name: string | null
      startDateLocal: string
      endDateLocal: string
      maxAttemptsDraft: number | null
      prizeDistribution: PrizeDistribution | null
    }

    const roundsState = reactive<{
      loading: boolean
      saving: boolean
      scheduleType: 'explicit-rounds' | 'periodic' | 'none' | null
      drafts: RoundDraft[]
      error: string | null
      toast: string | null
    }>({
      loading: false,
      saving: false,
      scheduleType: null,
      drafts: [],
      error: null,
      toast: null,
    })

    const resetRoundsState = () => {
      roundsState.loading = false
      roundsState.saving = false
      roundsState.scheduleType = null
      roundsState.drafts = []
      roundsState.error = null
      roundsState.toast = null
    }

    const toLocalDateTimeInput = (iso?: string | null): string => {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      const pad = (n: number) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    }

    const fromLocalDateTimeToIso = (local: string): string | null => {
      if (!local) return null
      const d = new Date(local)
      if (Number.isNaN(d.getTime())) return null
      return d.toISOString()
    }

    const roundDefToDraft = (r: RoundDef): RoundDraft => ({
      id: r.id,
      name: r.name ?? null,
      startDateLocal: toLocalDateTimeInput(r.startDate),
      endDateLocal: toLocalDateTimeInput(r.endDate),
      maxAttemptsDraft: typeof r.maxAttempts === 'number' && r.maxAttempts > 0 ? r.maxAttempts : null,
      prizeDistribution: r.prizeDistribution ?? null,
    })

    const loadRoundSchedule = async (campaignId: string) => {
      roundsState.loading = true
      roundsState.error = null
      roundsState.toast = null
      roundsState.drafts = []
      roundsState.scheduleType = null
      try {
        const result = await roundsService.getRoundSchedule(campaignId)
        roundsState.scheduleType = result.scheduleType
        if (result.scheduleType === 'explicit-rounds') {
          roundsState.drafts = result.periodicSchedule.rounds.map(roundDefToDraft)
        }
      } catch (err: unknown) {
        roundsState.error = err instanceof Error ? err.message : 'Failed to load rounds.'
      } finally {
        roundsState.loading = false
      }
    }

    const onRoundDateChange = (
      id: string,
      field: 'startDateLocal' | 'endDateLocal',
      value: string,
    ) => {
      const draft = roundsState.drafts.find(d => d.id === id)
      if (!draft) return
      draft[field] = value
      roundsState.toast = null
    }

    const onRoundMaxAttemptsChange = (id: string, raw: string) => {
      const draft = roundsState.drafts.find(d => d.id === id)
      if (!draft) return
      if (raw.trim() === '') {
        draft.maxAttemptsDraft = null
      } else {
        const parsed = parseInt(raw, 10)
        draft.maxAttemptsDraft = Number.isFinite(parsed) && parsed > 0 ? parsed : null
      }
      roundsState.toast = null
    }

    const roundsValidationError = computed<string | null>(() => {
      if (roundsState.scheduleType !== 'explicit-rounds') return null
      if (roundsState.drafts.length === 0) return null
      const parsed: Array<{ id: string; name: string | null; start: number; end: number }> = []
      for (const d of roundsState.drafts) {
        const startIso = fromLocalDateTimeToIso(d.startDateLocal)
        const endIso = fromLocalDateTimeToIso(d.endDateLocal)
        if (!startIso || !endIso) {
          return `${d.name || 'A round'} has an invalid date.`
        }
        const start = new Date(startIso).getTime()
        const end = new Date(endIso).getTime()
        if (end <= start) {
          return `${d.name || 'A round'} must end after it starts.`
        }
        parsed.push({ id: d.id, name: d.name, start, end })
      }
      parsed.sort((a, b) => a.start - b.start)
      for (let i = 1; i < parsed.length; i++) {
        if (parsed[i].start < parsed[i - 1].end) {
          return "Rounds cannot overlap. Adjust the dates so they don't overlap."
        }
      }
      return null
    })

    const saveRounds = async () => {
      if (!editingCampaignId.value) return
      if (roundsValidationError.value) return
      roundsState.saving = true
      roundsState.error = null
      roundsState.toast = null
      try {
        const payload = roundsState.drafts.map(d => ({
          id: d.id,
          startDate: fromLocalDateTimeToIso(d.startDateLocal) as string,
          endDate: fromLocalDateTimeToIso(d.endDateLocal) as string,
          maxAttempts: d.maxAttemptsDraft,
        }))
        const updated = await roundsService.patchRoundSchedule(editingCampaignId.value, payload)
        roundsState.drafts = updated.rounds.map(roundDefToDraft)
        roundsState.toast = 'Rounds saved.'
      } catch (err: unknown) {
        roundsState.error = err instanceof Error ? err.message : 'Failed to save rounds.'
      } finally {
        roundsState.saving = false
      }
    }

    // ─── Prize Distribution (Section 5) ─────────────────────────────────────
    // Per-round save state. Saving patches the round schedule with the updated
    // prizeDistribution via the existing patchRoundSchedule mechanism.

    const prizeDist = reactive<{
      saving: boolean
      errorByRound: Record<string, string>
      toastByRound: Record<string, string>
    }>({
      saving: false,
      errorByRound: {},
      toastByRound: {},
    })

    const resetPrizeDistState = () => {
      prizeDist.saving = false
      prizeDist.errorByRound = {}
      prizeDist.toastByRound = {}
    }

    const savePrizeDistribution = async (roundId: string, dist: PrizeDistribution) => {
      if (!editingCampaignId.value) return
      prizeDist.saving = true
      prizeDist.errorByRound = { ...prizeDist.errorByRound, [roundId]: '' }
      prizeDist.toastByRound = { ...prizeDist.toastByRound, [roundId]: '' }
      try {
        // Patch all rounds, injecting the updated prizeDistribution for the target round
        const payload = roundsState.drafts.map(d => ({
          id: d.id,
          startDate: fromLocalDateTimeToIso(d.startDateLocal) as string,
          endDate: fromLocalDateTimeToIso(d.endDateLocal) as string,
          maxAttempts: d.maxAttemptsDraft,
          prizeDistribution: d.id === roundId ? dist : (d.prizeDistribution ?? undefined),
        }))
        const updated = await roundsService.patchRoundSchedule(editingCampaignId.value, payload)
        roundsState.drafts = updated.rounds.map(roundDefToDraft)
        prizeDist.toastByRound = { ...prizeDist.toastByRound, [roundId]: 'Saved.' }
      } catch (err: unknown) {
        prizeDist.errorByRound = {
          ...prizeDist.errorByRound,
          [roundId]: err instanceof Error ? err.message : 'Failed to save prize distribution.',
        }
      } finally {
        prizeDist.saving = false
      }
    }

    /**
     * Parse a CSV file client-side.
     * Expects one code per line; trims whitespace; strips a header row if it
     * looks like a header (non-empty, doesn't look like a voucher code).
     */
    const parseCsvCodes = (text: string): string[] => {
      return text
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(line => line.length > 0)
    }

    const refreshPoolStatus = async (campaignId: string, prizeId: string) => {
      poolState.statusLoading = true
      poolState.statusError = null
      try {
        poolState.status = await prizesService.getPoolStatus(campaignId, prizeId)
        // Seed allotmentDraft from live allotment if not yet set
        if (poolState.allotmentDraft === null && poolState.status.allotment !== null) {
          poolState.allotmentDraft = poolState.status.allotment
        }
      } catch (err: unknown) {
        poolState.statusError = 'Unable to load pool status right now.'
        console.warn('Failed to load pool status:', err)
      } finally {
        poolState.statusLoading = false
      }
    }

    const loadPoolData = async (campaignId: string) => {
      poolState.prizesLoading = true
      try {
        const [prizes, dropPools] = await Promise.all([
          prizesService.listPrizes(campaignId),
          prizesService.listDropPools(campaignId),
        ])
        poolState.prizes = prizes
        poolState.dropPools = dropPools

        // Auto-select single prize / drop pool
        if (prizes.length === 1) {
          poolState.selectedPrizeId = prizes[0].id
          await refreshPoolStatus(campaignId, prizes[0].id)
        }
        if (dropPools.length === 1) {
          poolState.selectedDropPoolId = dropPools[0].id
        }
      } catch (err: unknown) {
        poolState.statusError = 'Unable to load prize data. You can still edit campaign details above.'
        console.warn('Failed to load prize/drop-pool lists:', err)
      } finally {
        poolState.prizesLoading = false
      }
    }

    const onPrizeSelected = async () => {
      if (!editingCampaignId.value || !poolState.selectedPrizeId) return
      poolState.status = null
      poolState.allotmentDraft = null
      // Reset code list
      poolState.codesOpen = false
      poolState.codesPage = []
      await refreshPoolStatus(editingCampaignId.value, poolState.selectedPrizeId)
    }

    const csvFileInput = ref<HTMLInputElement | null>(null)

    // Open the OS file picker directly from the "Upload CSV" button.
    const triggerCsvUpload = () => {
      poolState.addMode = 'upload'
      csvFileInput.value?.click()
    }

    const onCsvFileSelected = (event: Event) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]
      if (!file) return
      poolState.csvFileName = file.name
      poolState.csvCodes = []
      poolState.csvPreviewCount = null
      const reader = new FileReader()
      reader.onload = (e) => {
        const text = e.target?.result as string
        const codes = parseCsvCodes(text)
        poolState.csvCodes = codes
        poolState.csvPreviewCount = codes.length
      }
      reader.readAsText(file)
    }

    const showAddToast = (message: string) => {
      poolState.addToast = message
      setTimeout(() => { poolState.addToast = null }, 4000)
    }

    const submitAddCodes = async () => {
      if (!editingCampaignId.value || !poolState.selectedPrizeId) return
      if (!poolState.csvCodes.length) return
      poolState.addLoading = true
      poolState.addError = null
      try {
        const result = await prizesService.addVouchers(
          editingCampaignId.value,
          poolState.selectedPrizeId,
          { codes: poolState.csvCodes },
        )
        showAddToast(`Added ${result.added} codes (${result.skipped} duplicates skipped)`)
        // Reset the file input state
        poolState.csvCodes = []
        poolState.csvFileName = null
        poolState.csvPreviewCount = null
        await refreshPoolStatus(editingCampaignId.value, poolState.selectedPrizeId)
      } catch (err: unknown) {
        poolState.addError = err instanceof Error ? err.message : 'Failed to add codes.'
      } finally {
        poolState.addLoading = false
      }
    }

    const submitGenerateCodes = async () => {
      if (!editingCampaignId.value || !poolState.selectedPrizeId) return
      if (!poolState.generateCount || !poolState.generateLength) return
      poolState.addLoading = true
      poolState.addError = null
      try {
        const result = await prizesService.addVouchers(
          editingCampaignId.value,
          poolState.selectedPrizeId,
          {
            generate: {
              count: poolState.generateCount,
              length: poolState.generateLength,
              prefix: poolState.generatePrefix || undefined,
            },
          },
        )
        showAddToast(`Added ${result.added} codes (${result.skipped} duplicates skipped)`)
        await refreshPoolStatus(editingCampaignId.value, poolState.selectedPrizeId)
      } catch (err: unknown) {
        poolState.addError = err instanceof Error ? err.message : 'Failed to generate codes.'
      } finally {
        poolState.addLoading = false
      }
    }

    const confirmRemoveUnclaimed = async () => {
      if (!editingCampaignId.value || !poolState.selectedPrizeId) return
      poolState.removeUnclaimedLoading = true
      poolState.removeUnclaimedError = null
      poolState.removeUnclaimedToast = null
      try {
        const result = await prizesService.removeUnclaimedVouchers(
          editingCampaignId.value,
          poolState.selectedPrizeId,
        )
        poolState.removeUnclaimedConfirm = false
        poolState.removeUnclaimedToast = `Removed ${result.removed} unclaimed codes.`
        setTimeout(() => { poolState.removeUnclaimedToast = null }, 4000)
        await refreshPoolStatus(editingCampaignId.value, poolState.selectedPrizeId)
        if (poolState.codesOpen) {
          await loadCodesPage(1)
        }
      } catch (err: unknown) {
        poolState.removeUnclaimedError = err instanceof Error ? err.message : 'Failed to remove unclaimed codes.'
      } finally {
        poolState.removeUnclaimedLoading = false
      }
    }

    const loadCodesPage = async (page: number) => {
      if (!editingCampaignId.value || !poolState.selectedPrizeId) return
      poolState.codesLoading = true
      poolState.codesError = null
      try {
        const result = await prizesService.listVouchers(
          editingCampaignId.value,
          poolState.selectedPrizeId,
          page,
          25,
        )
        poolState.codesPage = result.items
        poolState.codesCurrentPage = page
        poolState.codesTotalPages = Math.max(1, Math.ceil(result.total / 25))
      } catch (err: unknown) {
        poolState.codesError = 'Failed to load voucher codes.'
        console.warn('Failed to load codes page:', err)
      } finally {
        poolState.codesLoading = false
      }
    }

    const toggleCodesAccordion = async () => {
      poolState.codesOpen = !poolState.codesOpen
      if (poolState.codesOpen && poolState.codesPage.length === 0) {
        await loadCodesPage(1)
      }
    }

    const removeCode = async (code: string) => {
      if (!editingCampaignId.value || !poolState.selectedPrizeId) return
      poolState.removingCode = code
      try {
        await prizesService.removeVoucher(
          editingCampaignId.value,
          poolState.selectedPrizeId,
          code,
        )
        // Remove from local page without a full reload for snappy UX
        poolState.codesPage = poolState.codesPage.filter(item => item.code !== code)
        await refreshPoolStatus(editingCampaignId.value, poolState.selectedPrizeId)
      } catch (err: unknown) {
        poolState.codesError = err instanceof Error ? err.message : 'Failed to remove code.'
      } finally {
        poolState.removingCode = null
      }
    }

    const saveAllotment = async () => {
      if (!editingCampaignId.value || !poolState.selectedDropPoolId) return
      if (!poolState.selectedPrizeId) {
        poolState.allotmentError = 'Select a prize before saving the allotment.'
        return
      }
      if (poolState.allotmentDraft === null) return
      // Clamp to [0, total] — browser max attribute is a hint, not enforced on direct typing.
      const total = poolState.status?.total ?? 0
      const clamped = Math.max(0, Math.min(poolState.allotmentDraft, total))
      if (clamped !== poolState.allotmentDraft) {
        poolState.allotmentDraft = clamped
      }
      poolState.allotmentLoading = true
      poolState.allotmentError = null
      poolState.allotmentToast = null
      try {
        await prizesService.patchDropPoolAllotment(
          editingCampaignId.value,
          poolState.selectedDropPoolId,
          poolState.selectedPrizeId,
          clamped,
        )
        poolState.allotmentToast = 'Allotment saved.'
        setTimeout(() => { poolState.allotmentToast = null }, 3000)
        // Refresh counter strip to show new allotment value
        if (poolState.selectedPrizeId) {
          await refreshPoolStatus(editingCampaignId.value, poolState.selectedPrizeId)
        }
      } catch (err: unknown) {
        poolState.allotmentError = err instanceof Error ? err.message : 'Failed to save allotment.'
      } finally {
        poolState.allotmentLoading = false
      }
    }
    // ─── End of pool state + actions ───────────────────────────────────────

    // Attach-experience receive flow: populated from ?attachExperience= query param
    const attachExperienceKey = ref<string | null>(null)
    const attachExperienceName = ref<string | null>(null)

    // Confirmation modal state for direct-attach flow (Package H)
    const attachConfirmCardRef = ref<HTMLElement | null>(null)
    const attachConfirm = ref<{
      open: boolean
      campaignId: string | null
      campaignName: string
      experienceName: string
      saving: boolean
      error: string | null
    }>({ open: false, campaignId: null, campaignName: '', experienceName: '', saving: false, error: null })

    // Keep in sync with FormView.vue:924 buildAttachedExperienceReference
    const buildAttachedExperienceReference = (
      record?: ExperienceLibraryRecord | null,
    ): AttachedExperienceReference | null => {
      if (!record || record.status !== 'published') {
        return null
      }
      return {
        source: 'shared-published',
        localKey: record.localKey,
        experienceId: record.localKey,
        lineageId: record.lineageId || record.quizConfig.experienceDefinitionId || record.localKey,
        versionId: record.quizConfig.experienceDefinitionVersionId || (record.versionNumber ? `v${record.versionNumber}` : 'v1'),
        versionNumber: record.versionNumber || 1,
        name: record.name,
        status: 'published',
        templateFamilyId: record.templateFamilyId || record.quizConfig.experienceTemplateFamilyId || record.quizConfig.quizTemplateId,
        experienceMode: record.experienceMode || record.quizConfig.experienceMode || 'quiz',
      }
    }

    const visibleCampaigns = computed(() => (
      selectedFilter.value === 'All'
        ? campaigns.value
        : campaigns.value.filter(campaign => campaign.lifecycleStatus === selectedFilter.value)
    ))

    const filterCounts = computed<Record<CampaignFilter, number>>(() => ({
      All: campaigns.value.length,
      Active: campaigns.value.filter(campaign => campaign.lifecycleStatus === 'Active').length,
      Scheduled: campaigns.value.filter(campaign => campaign.lifecycleStatus === 'Scheduled').length,
      Paused: campaigns.value.filter(campaign => campaign.lifecycleStatus === 'Paused').length,
      Ended: campaigns.value.filter(campaign => campaign.lifecycleStatus === 'Ended').length,
      Unscheduled: campaigns.value.filter(campaign => campaign.lifecycleStatus === 'Unscheduled').length,
    }))

    const lastUpdatedLabel = computed(() => {
      if (!lastUpdatedAt.value) {
        return ''
      }

      const now = Date.now()
      const elapsedMs = Math.max(0, now - lastUpdatedAt.value)
      const elapsedMinutes = Math.floor(elapsedMs / 60000)

      if (elapsedMinutes < 1) {
        return 'Updated just now'
      }
      if (elapsedMinutes < 60) {
        return `Updated ${elapsedMinutes} min ago`
      }

      return `Updated ${new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(lastUpdatedAt.value))}`
    })

    const timezoneOptions = computed(() => {
      const intlWithSupportedValues = Intl as typeof Intl & {
        supportedValuesOf?: (key: string) => string[]
      }
      const fromIntl = typeof Intl !== 'undefined' && 'supportedValuesOf' in intlWithSupportedValues
        ? intlWithSupportedValues.supportedValuesOf?.('timeZone') ?? []
        : []

      return Array.from(new Set([
        'UTC',
        ...fromIntl,
        ...FALLBACK_TIMEZONES,
      ])).sort((a, b) => a.localeCompare(b))
    })

    const formatDate = (value: string) => {
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) {
        return value
      }
      return new Intl.DateTimeFormat(undefined, DATE_FORMAT_OPTIONS).format(parsed)
    }

    const formatDateTime = (value?: string | null) => {
      if (!value) {
        return UNSCHEDULED_LABEL
      }
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) {
        return value
      }
      return new Intl.DateTimeFormat(undefined, DATE_TIME_FORMAT_OPTIONS).format(parsed)
    }

    const formatNumber = (value: number) => new Intl.NumberFormat().format(value)

    const replaceCampaign = (nextCampaign: ManagedCampaign) => {
      const updated = toDashboardCampaign(nextCampaign)
      const existing = campaigns.value.find(item => item.id === updated.id)
      if (existing) {
        updated.metrics = existing.metrics
      }
      campaigns.value = campaigns.value
        .map(item => item.id === updated.id ? updated : item)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    }

    const loadCampaigns = async () => {
      isLoading.value = true
      loadError.value = ''
      analyticsWarning.value = ''
      isDashboardMenuOpen.value = false

      try {
        const managedCampaigns = await campaignsService.listCampaigns()
        campaigns.value = managedCampaigns
          .map(toDashboardCampaign)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        lastUpdatedAt.value = Date.now()
      } catch (error: unknown) {
        console.warn('Failed to load campaigns:', error)
        loadError.value = 'Unable to load campaigns right now.'
      } finally {
        isLoading.value = false
      }

      if (campaigns.value.length === 0) {
        return
      }

      try {
        const analytics = await analyticsService.getCampaignAnalyticsBatch(campaigns.value.map(campaign => campaign.id))
        const analyticsByCampaignId = new Map(analytics.map(item => [item.campaignId, item]))

        campaigns.value = campaigns.value.map(campaign => {
          const metrics = analyticsByCampaignId.get(campaign.id)
          return {
            ...campaign,
            metrics: metrics
              ? {
                  registered: metrics.registered,
                  claimed: metrics.claimed,
                  redeemed: metrics.redeemed,
                  shared: metrics.shared,
                }
              : campaign.metrics,
          }
        })
      } catch (error: unknown) {
        console.warn('Failed to load campaign analytics batch:', error)
        analyticsWarning.value = 'Campaigns are shown, but live tallies are temporarily unavailable.'
      }
    }

    // Gameplays is NOT part of the lightweight Valkey counts batch that feeds the
    // other card tallies; it lives in the historical analytics lake and is fetched
    // per-campaign. To avoid firing one lake query per card on every dashboard load,
    // we lazy-load it: an IntersectionObserver only triggers the query when a row
    // scrolls into view. State is held here (not on campaign.metrics) so the data
    // model shared with the analytics views stays untouched.
    const gameplaysState = reactive<Record<string, { loading: boolean; value: number | null }>>({})

    const loadGameplays = async (campaignId: string): Promise<void> => {
      if (gameplaysState[campaignId]) {
        return
      }
      gameplaysState[campaignId] = { loading: true, value: null }
      const campaign = campaigns.value.find(item => item.id === campaignId)
      try {
        const response = await analyticsService.queryHistorical({
          campaignId,
          from: campaign?.startDate ?? undefined,
          to: new Date().toISOString(),
          maxObjects: 400,
        })
        gameplaysState[campaignId] = {
          loading: false,
          value: response.familyTotals?.gameplays ?? 0,
        }
      } catch (error: unknown) {
        console.warn('Failed to load gameplays for campaign', { campaignId, error })
        gameplaysState[campaignId] = { loading: false, value: null }
      }
    }

    const gameplaysDisplay = (campaignId: string): string => {
      const state = gameplaysState[campaignId]
      if (!state || state.loading) {
        return '…'
      }
      if (state.value === null) {
        return '—'
      }
      return formatNumber(state.value)
    }

    let gameplaysRowObserver: IntersectionObserver | null = null

    const handleGameplaysRowIntersect: IntersectionObserverCallback = (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) {
          continue
        }
        const el = entry.target as HTMLElement
        const campaignId = el.dataset.campaignId
        gameplaysRowObserver?.unobserve(el)
        if (campaignId) {
          void loadGameplays(campaignId)
        }
      }
    }

    const setGameplaysRowRef = (
      el: Element | ComponentPublicInstance | null,
      campaign: DashboardCampaign
    ): void => {
      if (!el || !(el instanceof HTMLElement)) {
        return
      }
      if (!gameplaysRowObserver) {
        gameplaysRowObserver = new IntersectionObserver(handleGameplaysRowIntersect, {
          rootMargin: '120px',
          threshold: 0.01,
        })
      }
      el.dataset.campaignId = campaign.id
      gameplaysRowObserver.observe(el)
    }

    onBeforeUnmount(() => {
      gameplaysRowObserver?.disconnect()
      gameplaysRowObserver = null
    })

    const closeEditModal = () => {
      isEditModalOpen.value = false
      editingCampaignId.value = null
      editCampaignName.value = ''
      editStartDate.value = ''
      editEndDate.value = ''
      editTimezone.value = ''
      editError.value = ''
      resetPoolState()
      resetRoundsState()
      resetPrizeDistState()
    }

    const closeCampaignMenu = () => {
      openMenuCampaignId.value = null
      openMenuDirection.value = 'down'
    }

    const closeDashboardMenu = () => {
      isDashboardMenuOpen.value = false
    }

    const closeOpenMenus = () => {
      closeDashboardMenu()
      closeCampaignMenu()
    }

    const toggleCampaignMenu = (campaignId: string, event?: MouseEvent) => {
      isDashboardMenuOpen.value = false
      if (openMenuCampaignId.value === campaignId) {
        closeCampaignMenu()
        return
      }

      const trigger = event?.currentTarget instanceof HTMLElement ? event.currentTarget : null
      if (trigger) {
        const rect = trigger.getBoundingClientRect()
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
        const estimatedMenuHeight = window.innerWidth <= 896 ? 22 * 16 : 18 * 16
        const spaceBelow = viewportHeight - rect.bottom
        const spaceAbove = rect.top
        openMenuDirection.value = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow ? 'up' : 'down'
      } else {
        openMenuDirection.value = 'down'
      }

      openMenuCampaignId.value = campaignId
    }

    const toggleDashboardMenu = () => {
      closeCampaignMenu()
      isDashboardMenuOpen.value = !isDashboardMenuOpen.value
    }

    const openEditModal = (campaign: DashboardCampaign) => {
      closeCampaignMenu()
      editingCampaignId.value = campaign.id
      editCampaignName.value = campaign.name || ''
      editStartDate.value = toDateInputValue(campaign.startDate)
      editEndDate.value = toDateInputValue(campaign.endDate)
      editTimezone.value = campaign.timezone || 'UTC'
      editError.value = ''
      isEditModalOpen.value = true
      // Load pool data in the background — failures show inline error, don't block
      void loadPoolData(campaign.id)
      // Same lazy-load pattern for the round schedule.
      void loadRoundSchedule(campaign.id)
    }

    const saveEditedCampaign = async () => {
      if (!editingCampaignId.value) {
        editError.value = 'No campaign selected.'
        return
      }

      if (!editCampaignName.value.trim()) {
        editError.value = 'Campaign name is required.'
        return
      }

      const startIso = fromDateInputToIso(editStartDate.value)
      const endIso = fromDateInputToIso(editEndDate.value)

      if (editStartDate.value && !startIso) {
        editError.value = 'Please select a valid start date.'
        return
      }
      if (editEndDate.value && !endIso) {
        editError.value = 'Please select a valid end date.'
        return
      }
      if (startIso && endIso && new Date(endIso).getTime() <= new Date(startIso).getTime()) {
        editError.value = 'End date must be after the start date.'
        return
      }

      isSavingEdit.value = true
      editError.value = ''

      try {
        const updated = await campaignsService.updateCampaign(editingCampaignId.value, {
          name: editCampaignName.value.trim(),
          startDate: startIso,
          endDate: endIso,
          timezone: editTimezone.value.trim() || 'UTC',
        })
        replaceCampaign(updated)
        closeEditModal()
      } catch (error: unknown) {
        console.warn('Failed to update campaign:', error)
        editError.value = 'Unable to save campaign changes right now.'
      } finally {
        isSavingEdit.value = false
      }
    }

    const loadUsage = async () => {
      try {
        usage.value = await billingService.getOrgUsage()
      } catch (error: unknown) {
        console.warn('Failed to load org usage', error)
        // Non-fatal — UI works without banner.
      }
    }

    const loadCapStatus = async () => {
      try {
        capStatus.value = await campaignsService.getCapStatus()
      } catch (error: unknown) {
        console.warn('Failed to load cap status', error)
        // Graceful degradation — don't render the banner if the endpoint is unreachable.
        capStatus.value = null
      }
    }

    const toggleCapBannerSelection = (campaignId: string) => {
      const next = new Set(capBannerSelection.value)
      if (next.has(campaignId)) {
        next.delete(campaignId)
      } else {
        next.add(campaignId)
      }
      capBannerSelection.value = next
    }

    const applyOverCapDeactivate = async () => {
      if (!capStatus.value || capBannerSelection.value.size === 0 || isDeactivatingOverCap.value) return
      isDeactivatingOverCap.value = true
      overCapDeactivateResults.value = []
      overCapErrorToast.value = null

      const ids = Array.from(capBannerSelection.value)
      const results = await Promise.allSettled(
        ids.map(id => campaignsService.deactivateCampaign(id).then(updated => ({ id, updated })))
      )

      const outcome: { id: string; name: string; success: boolean; error?: string }[] = []
      for (let i = 0; i < results.length; i++) {
        const result = results[i]
        const id = ids[i]
        const campaign = campaigns.value.find(c => c.id === id)
        const name = campaign?.name || id
        if (result.status === 'fulfilled') {
          replaceCampaign(result.value.updated)
          outcome.push({ id, name, success: true })
        } else {
          outcome.push({ id, name, success: false, error: 'Deactivation failed' })
        }
      }
      overCapDeactivateResults.value = outcome

      const anyFailed = outcome.some(r => !r.success)
      if (anyFailed) {
        overCapErrorToast.value = 'Some campaigns could not be deactivated — check results below.'
      }

      capBannerSelection.value = new Set()
      await Promise.all([loadCapStatus(), loadUsage()])
      isDeactivatingOverCap.value = false
    }

    const togglePaused = async (campaign: DashboardCampaign) => {
      closeCampaignMenu()
      pendingCampaignId.value = campaign.id
      try {
        if (campaign.paused) {
          // Activate → reserve slot. If at cap, backend returns 402 + payload.
          try {
            const updated = await campaignsService.activateCampaign(campaign.id)
            replaceCampaign(updated)
          } catch (error: any) {
            const response = error?.response
            if (response?.status === 402 && response?.data?.data?.code === 'CAMPAIGN_LIMIT_REACHED') {
              openSwapModal(campaign, response.data.data.activeCount ?? 0, response.data.data.limit ?? 0)
              return
            }
            throw error
          }
        } else {
          const updated = await campaignsService.deactivateCampaign(campaign.id)
          replaceCampaign(updated)
        }
        await loadUsage()
      } catch (error: any) {
        if (error?.response?.status === 404) {
          console.warn('Campaign org record missing for campaign', campaign.id, error)
          loadError.value = 'Campaign organisation record not found — contact support.'
          return
        }
        console.warn('Failed to update campaign activation state:', error)
        loadError.value = 'Unable to update campaign activation state right now.'
      } finally {
        pendingCampaignId.value = null
      }
    }

    const openSwapModal = (target: DashboardCampaign, activeCount: number, limit: number) => {
      swapModal.value = {
        open: true,
        targetCampaignId: target.id,
        targetName: target.name || 'Untitled campaign',
        activeCount,
        limit,
      }
      swapSelection.value = null
    }

    const closeSwapModal = () => {
      swapModal.value = { open: false, targetCampaignId: null, targetName: '', activeCount: 0, limit: 0 }
      swapSelection.value = null
      isSwapping.value = false
    }

    const applySwap = async () => {
      const targetId = swapModal.value.targetCampaignId
      const deactivateId = swapSelection.value
      if (!targetId || !deactivateId || isSwapping.value) return
      isSwapping.value = true
      try {
        const { activated, deactivated } = await campaignsService.swapActiveCampaign(targetId, deactivateId)
        replaceCampaign(activated)
        replaceCampaign(deactivated)
        await loadUsage()
        closeSwapModal()
      } catch (error: unknown) {
        console.warn('Swap failed', error)
        loadError.value = 'Unable to swap campaigns right now.'
        isSwapping.value = false
      }
    }

    const swapCandidates = computed(() =>
      campaigns.value.filter(c =>
        c.lifecycleStatus === 'Active' && c.id !== swapModal.value.targetCampaignId
      )
    )

    // Campaigns that count as "Active" per frontend lifecycle derivation — used by the over-cap banner
    const activeCampaigns = computed(() =>
      campaigns.value.filter(c => c.lifecycleStatus === 'Active')
    )

    const openStandardAnalytics = async (campaign: DashboardCampaign) => {
      closeCampaignMenu()
      await router.push({
        name: 'DashboardAnalytics',
        query: {
          campaignId: campaign.id,
          mode: 'standard',
        },
      })
    }

    const startNewCampaign = async () => {
      localStorage.removeItem(CAMPAIGN_BUILDER_STATE_KEY)
      campaignBuilderStore.$reset()
      uiStore.$reset()
      formStore.reset()
      await router.push({ path: '/' })
    }

    const startNewCampaignFromMenu = async () => {
      closeDashboardMenu()
      await startNewCampaign()
    }

    const openExperienceLibrary = async () => {
      closeDashboardMenu()
      await router.push({ path: '/dashboard/experiences' })
    }

    const copyShareLink = async (campaign: DashboardCampaign) => {
      closeCampaignMenu()
      const copied = await copyToClipboard(campaign.openCampaignUrl)
      if (!copied) {
        return
      }
      copiedCampaignId.value = campaign.id
      if (copyResetTimeout !== null) {
        window.clearTimeout(copyResetTimeout)
      }
      copyResetTimeout = window.setTimeout(() => {
        copiedCampaignId.value = null
      }, 1800)
    }

    const openEditFromMenu = (campaign: DashboardCampaign) => {
      openEditModal(campaign)
    }

    const clearAttachParam = () => {
      attachExperienceKey.value = null
      attachExperienceName.value = null
      void router.replace({ query: { ...route.query, attachExperience: undefined } })
    }

    const openAttachConfirm = (campaign: DashboardCampaign) => {
      if (!attachExperienceKey.value) return
      attachConfirm.value = {
        open: true,
        campaignId: campaign.id,
        campaignName: campaign.name || 'Untitled campaign',
        experienceName: attachExperienceName.value || attachExperienceKey.value,
        saving: false,
        error: null,
      }
      void nextTick(() => {
        attachConfirmCardRef.value?.focus()
      })
    }

    const closeAttachConfirm = () => {
      if (attachConfirm.value.saving) return
      attachConfirm.value = { ...attachConfirm.value, open: false, error: null }
    }

    const confirmAttach = async () => {
      if (!attachConfirm.value.campaignId || !attachExperienceKey.value) return
      const record = getExperienceLibraryRecord(attachExperienceKey.value)
      if (!record) {
        attachConfirm.value.error = "Couldn't resolve experience — please return to the editor and try again."
        return
      }
      const attachedExperience = buildAttachedExperienceReference(record)
      if (!attachedExperience) {
        attachConfirm.value.error = "Experience must be published before it can be attached."
        return
      }
      attachConfirm.value.saving = true
      attachConfirm.value.error = null
      try {
        await campaignsService.attachExperience(attachConfirm.value.campaignId, attachedExperience)
        attachConfirm.value.open = false
        clearAttachParam()
        void loadCampaigns()
      } catch (err) {
        attachConfirm.value.error = err instanceof Error ? err.message : 'Failed to attach experience. Please try again.'
        attachConfirm.value.saving = false
      }
    }


    const togglePausedFromMenu = async (campaign: DashboardCampaign) => {
      await togglePaused(campaign)
    }

    // "View results" — gated to experience types that actually carry
    // per-user response data worth viewing. Survey, quiz, and (upcoming)
    // game. Anything else (ar, location, …) hides the menu item entirely
    // so the dashboard stays uncluttered.
    //
    // Legacy campaigns created before experienceType was persisted on the
    // Campaign record (backend commit 98b3ff7f, 2026-04-21) return null
    // here and will NOT show the button until they're re-customised. That
    // was the accepted trade vs. a noisy universal show — new campaigns
    // work immediately, old ones need a no-op re-save.
    const RESULTS_SUPPORTED_TYPES = new Set(['survey', 'quiz', 'game'])

    const supportsResults = (campaign: DashboardCampaign) => {
      const t = (campaign as DashboardCampaign & { experienceType?: string | null }).experienceType
      return typeof t === 'string' && RESULTS_SUPPORTED_TYPES.has(t)
    }

    const openResponsesFromMenu = (campaign: DashboardCampaign) => {
      closeCampaignMenu()
      responsesModal.value = {
        visible: true,
        campaignId: campaign.id,
        campaignName: campaign.name || null,
      }
    }

    const closeResponsesModal = () => {
      responsesModal.value = { visible: false, campaignId: null, campaignName: null }
    }

    // The campaign currently open in the edit modal — used to conditionally
    // show the Survey Responses panel inside that modal.
    const editingCampaign = computed<DashboardCampaign | null>(
      () => campaigns.value.find((c) => c.id === editingCampaignId.value) ?? null,
    )


    const copyShareLinkFromMenu = async (campaign: DashboardCampaign) => {
      await copyShareLink(campaign)
    }

    const ensureAccess = async (): Promise<boolean> => {
      await authStore.checkAuth()
      if (!authStore.isAuthenticated) {
        await router.replace({ path: '/' })
        return false
      }
      return true
    }

    onMounted(async () => {
      window.addEventListener('click', closeCampaignMenu)
      window.addEventListener('click', closeDashboardMenu)

      // Resolve ?attachExperience= param defensively
      const attachParam = typeof route.query.attachExperience === 'string' ? route.query.attachExperience : null
      if (attachParam) {
        const record = getExperienceLibraryRecord(attachParam)
        if (record) {
          attachExperienceKey.value = attachParam
          attachExperienceName.value = record.name || attachParam
        }
      }

      const canAccess = await ensureAccess()
      if (canAccess) {
        await Promise.all([loadCampaigns(), loadUsage(), loadCapStatus()])
      }
    })

    onBeforeUnmount(() => {
      window.removeEventListener('click', closeCampaignMenu)
      window.removeEventListener('click', closeDashboardMenu)
      if (copyResetTimeout !== null) {
        window.clearTimeout(copyResetTimeout)
      }
    })

    return {
      closeEditModal,
      copiedCampaignId,
      copyShareLink,
      editCampaignName,
      editEndDate,
      editError,
      editStartDate,
      editTimezone,
      analyticsWarning,
      timezoneOptions,
      filters,
      filterCounts,
      formatDate,
      formatDateTime,
      formatNumber,
      gameplaysDisplay,
      setGameplaysRowRef,
      isEditModalOpen,
      isLoading,
      isSavingEdit,
      isDashboardMenuOpen,
      lastUpdatedLabel,
      loadCampaigns,
      loadError,
      closeCampaignMenu,
      closeOpenMenus,
      copyShareLinkFromMenu,
      openEditFromMenu,
      openExperienceLibrary,
      openMenuCampaignId,
      openMenuDirection,
      openEditModal,
      openStandardAnalytics,
      pendingCampaignId,
      saveEditedCampaign,
      selectedFilter,
      startNewCampaign,
      startNewCampaignFromMenu,
      statusClassMap,
      toggleDashboardMenu,
      toggleCampaignMenu,
      togglePausedFromMenu,
      togglePaused,
      visibleCampaigns,
      usage,
      swapModal,
      swapSelection,
      swapCandidates,
      applySwap,
      closeSwapModal,
      isSwapping,
      supportsResults,
      openResponsesFromMenu,
      closeResponsesModal,
      responsesModal,
      editingCampaign,
      attachExperienceKey,
      attachExperienceName,
      attachConfirm,
      attachConfirmCardRef,
      openAttachConfirm,
      closeAttachConfirm,
      confirmAttach,
      clearAttachParam,
      capStatus,
      capBannerSelection,
      isDeactivatingOverCap,
      overCapDeactivateResults,
      overCapErrorToast,
      activeCampaigns,
      toggleCapBannerSelection,
      applyOverCapDeactivate,
      // Pool state + actions
      poolState,
      onPrizeSelected,
      csvFileInput,
      triggerCsvUpload,
      onCsvFileSelected,
      submitAddCodes,
      submitGenerateCodes,
      confirmRemoveUnclaimed,
      toggleCodesAccordion,
      loadCodesPage,
      removeCode,
      saveAllotment,
      // Rounds state + actions
      roundsState,
      roundsValidationError,
      onRoundDateChange,
      onRoundMaxAttemptsChange,
      saveRounds,
      // Prize Distribution state + actions (Section 5)
      prizeDist,
      savePrizeDistribution,
    }
  },
})
</script>

<style scoped>
.dashboard-container {
  display: flex;
  flex-direction: column;
  width: 96%;
  max-width: 88rem;
  padding: 1.75rem;
  margin: 6.25rem auto 3.125rem;
  background-color: var(--ui-inverse-surface);
  color: var(--vt-c-white);
  border-radius: 1rem;
  box-shadow:
    0 0.625rem 1.875rem rgba(0, 0, 0, 0.3),
    0 0 0 0.0625rem rgba(var(--vt-c-accent-rgb), 0.1);
  backdrop-filter: blur(0.625rem);
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.dashboard-heading {
  min-width: 0;
}

.dashboard-header h2 {
  margin: 0;
  color: var(--vt-c-white);
  font-size: 1.75rem;
  font-weight: 700;
  font-family: var(--font-sans);
  letter-spacing: -0.01em;
}

.dashboard-menu {
  position: relative;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: flex-start;
  justify-content: center;
}

.dashboard-menu-panel {
  position: absolute;
  top: calc(100% + 0.35rem);
  right: 0;
  z-index: 20;
  min-width: 13rem;
  display: grid;
  gap: 0.18rem;
  padding: 0.5rem;
  border-radius: 0.9rem;
  border: 0.0625rem solid rgba(var(--vt-c-accent-rgb), 0.4);
  background-color: var(--ui-inverse-surface);
  background-image: none;
  box-shadow:
    0 0 0 1px rgba(var(--vt-c-accent-rgb), 0.25),
    0 1.5rem 2.5rem rgba(10, 12, 20, 0.65),
    inset 0 0 40px rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(1.5rem) saturate(1.2);
  animation: dashboard-pop-in 0.3s cubic-bezier(0.15, 1.8, 0.4, 1);
}

.dashboard-menu-item {
  display: block;
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 0;
  border-radius: 0.65rem;
  outline: none;
  box-shadow: none;
  appearance: none;
  background: var(--vt-c-accent);
  color: var(--color-background);
  text-decoration: none;
  font-size: 0.82rem;
  font-weight: 500;
  text-align: center;
  font-family: var(--ui-inverse-font-family);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.dashboard-menu-item:hover {
  background-color: var(--ui-inverse-cta-bg-hover);
  color: var(--color-background);
  transform: scale(1.03);
}

.dashboard-section {
  padding: 1.1rem;
  background: var(--ui-inverse-surface);
  border-radius: 1rem;
  border: 0.0625rem solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.12);
}

.dashboard-section + .dashboard-section {
  margin-top: 1rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.section-header h3 {
  margin: 0;
  color: var(--vt-c-white);
  font-size: 1.1rem;
  font-weight: 700;
}

.section-subtitle {
  margin: 0.35rem 0 0.75rem;
  color: var(--ui-inverse-text-muted);
  font-size: 0.88rem;
}

.analytics-entry {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.analytics-entry-copy h3 {
  margin: 0 0 0.4rem;
  color: var(--vt-c-white);
  font-size: 1.1rem;
}

.analytics-entry-copy p {
  margin: 0;
  color: var(--ui-inverse-text-muted);
  line-height: 1.45;
}

.filter-toolbar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 0.55rem 0 0.9rem;
  min-height: 1.75rem;
}

.filter-label {
  flex: 0 0 auto;
  color: var(--ui-inverse-text-muted);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.filter-row {
  flex: 0 1 auto;
  display: grid;
  grid-template-columns: repeat(5, 5.2rem);
  gap: 0.3rem;
  min-width: 0;
  justify-content: center;
}

.refresh-icon-button {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  width: 5.2rem;
  min-height: 1.056rem;
  padding: 0.051rem 0.32rem;
  border-radius: 0.45rem;
  border: 0.0625rem solid var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: var(--color-background);
  font-size: 0.72rem;
  font-weight: 500;
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
  transition:
    border-color 0.18s ease,
    background-color 0.18s ease,
    color 0.18s ease;
}

.refresh-icon-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.refresh-icon-button:hover {
  background: var(--ui-inverse-cta-bg-hover);
  color: var(--color-background);
}

.refresh-icon {
  width: 0.78rem;
  height: 0.78rem;
  border: 0.1rem solid rgba(24, 27, 47, 0.34);
  border-top-color: var(--color-background);
  border-radius: 999px;
}

.refresh-icon-button.spinning .refresh-icon {
  animation: dashboard-spin 0.8s linear infinite;
}

.refresh-button-text {
  color: var(--color-background);
  font-size: 0.72rem;
  line-height: 1;
}

.secondary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.4rem;
  padding: 0.55rem 0.95rem;
  border-radius: 999px;
  border: 0.0625rem solid rgba(255, 255, 255, 0.22);
  background: transparent;
  color: var(--ui-inverse-text-muted);
  font-size: 0.86rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  text-decoration: none;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.secondary-button:hover {
  border-color: rgba(255, 255, 255, 0.4);
  color: var(--vt-c-white);
}

.filter-chip {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  min-height: 1.056rem;
  padding: 0.051rem 0.32rem;
  border-radius: 0.45rem;
  border: 0.0625rem solid var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: var(--color-background);
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
  transition:
    border-color 0.18s ease,
    background-color 0.18s ease,
    color 0.18s ease;
}

.filter-chip-text {
  display: inline-flex;
  align-items: center;
}

.filter-chip-count {
  color: var(--color-background);
  font-size: 0.68rem;
}

.filter-chip.active {
  background: var(--vt-c-accent);
  border-color: var(--vt-c-accent);
  color: var(--color-background);
  font-weight: 700;
}

.filter-chip.active .filter-chip-count {
  color: var(--color-background);
  font-weight: 700;
}

.filter-chip.active[data-filter='Active'] {
  background: #22c55e;
  border-color: #4ade80;
  color: #062814;
}

.filter-chip.active[data-filter='Paused'] {
  background: #facc15;
  border-color: #fde047;
  color: #422006;
}

.filter-chip.active[data-filter='Ended'] {
  background: #ef4444;
  border-color: #f87171;
  color: #ffffff;
}

.filter-chip.active[data-filter='Scheduled'] {
  background: #38bdf8;
  border-color: #7dd3fc;
  color: #082f49;
}

.filter-chip.active[data-filter='Unscheduled'] {
  background: #94a3b8;
  border-color: #cbd5e1;
  color: #0f172a;
}

.filter-chip:hover {
  background: var(--ui-inverse-cta-bg-hover);
  color: var(--color-background);
}

.dashboard-menu.open .hamburger,
.row-menu.open .hamburger,
.hamburger.menu-open {
  background: var(--vt-c-accent);
  border-color: var(--vt-c-accent);
}

.filter-chip:disabled,
.secondary-button:disabled {
  opacity: 0.65;
  cursor: default;
}

.error-banner {
  margin-bottom: 1rem;
  padding: 0.8rem 0.95rem;
  border-radius: 0.75rem;
  background: rgba(244, 63, 94, 0.1);
  border: 0.0625rem solid rgba(244, 63, 94, 0.25);
  color: var(--vt-c-white);
  font-size: 0.88rem;
  line-height: 1.4;
}

.info-banner {
  margin-bottom: 1rem;
  padding: 0.75rem 0.95rem;
  border-radius: 0.75rem;
  background: rgba(245, 158, 11, 0.07);
  border: 0.0625rem solid rgba(245, 158, 11, 0.16);
  color: var(--vt-c-white);
  font-size: 0.86rem;
  line-height: 1.4;
}

.campaign-list {
  display: grid;
  gap: 0.55rem;
}

.campaign-list-header,
.campaign-row {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(8.5rem, 0.7fr) minmax(13rem, 1.15fr) minmax(12rem, 1fr) auto;
  gap: 1rem;
  align-items: center;
}

.campaign-list-header {
  padding: 0 0.2rem 0.35rem;
  color: var(--vt-c-white);
  font-size: 0.67rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  opacity: 0.55;
}

.campaign-list-heading-actions {
  text-align: right;
}

.campaign-list-heading-status {
  text-align: left;
}

.campaign-row {
  background-color: rgba(15, 23, 42, 0.35);
  border-radius: 0.75rem;
  padding: 0.95rem 1rem;
  border: 0.0625rem solid rgba(255, 255, 255, 0.09);
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.campaign-row:hover {
  background-color: rgba(15, 23, 42, 0.5);
  border-color: rgba(var(--vt-c-accent-rgb), 0.2);
}

.campaign-primary {
  min-width: 0;
}

.campaign-title-row {
  display: flex;
  align-items: center;
  min-width: 0;
}

.campaign-title {
  margin: 0;
  font-size: 0.94rem;
  font-weight: 600;
  color: var(--vt-c-white);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.campaign-meta-row {
  margin-top: 0.22rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
}

.campaign-meta-label,
.campaign-meta-text,
.campaign-meta-separator {
  color: var(--ui-inverse-text-muted);
  font-size: 0.75rem;
}

.campaign-meta-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.campaign-status-column {
  display: flex;
  align-items: center;
  min-width: 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 99rem;
  min-height: 1.5rem;
  padding: 0.24rem 0.62rem;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  border: 1px solid transparent;
  background: var(--vt-c-accent);
  color: var(--color-background);
}

.status-badge.active {
  background: #22c55e;
  border-color: #4ade80;
  color: #062814;
}

.status-badge.scheduled {
  background: #38bdf8;
  border-color: #7dd3fc;
  color: #082f49;
}

.status-badge.paused {
  background: #facc15;
  border-color: #fde047;
  color: #422006;
}

.status-badge.ended {
  background: #ef4444;
  border-color: #f87171;
  color: #ffffff;
}

.status-badge.unscheduled {
  background: #94a3b8;
  border-color: #cbd5e1;
  color: #0f172a;
}

.campaign-schedule {
  display: grid;
  gap: 0.3rem;
}

.schedule-line {
  display: flex;
  justify-content: space-between;
  gap: 0.8rem;
}

.schedule-label {
  color: var(--ui-inverse-text-muted);
  font-size: 0.72rem;
}

.schedule-value {
  color: var(--vt-c-white);
  font-weight: 600;
  text-align: right;
  font-size: 0.76rem;
}

.metrics-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}

.metric-inline-item {
  min-width: 0;
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  border: 0.0625rem solid rgba(255, 255, 255, 0.12);
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
}

.metric-inline-label {
  font-size: 0.67rem;
  color: var(--ui-inverse-text-muted);
  font-weight: 500;
}

.metric-inline-value {
  font-size: 0.8rem;
  color: var(--vt-c-white);
  font-weight: 700;
}

.row-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.45rem;
  align-self: center;
}

.row-menu {
  position: relative;
  display: inline-flex;
  align-items: flex-start;
  justify-content: center;
}

.hamburger {
  border: 0.0625rem solid var(--vt-c-accent);
  padding: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.18rem;
  line-height: 0;
  cursor: pointer;
  width: 2.25rem;
  height: 2.25rem;
  margin: 0;
  background: var(--vt-c-accent);
  backdrop-filter: none;
  border-radius: 999px;
  box-shadow: none;
  overflow: visible;
}

.hamburger:hover {
  transform: none;
  box-shadow: none;
  background: var(--ui-inverse-cta-bg-hover);
}

.hamburger span {
  height: 0.125rem;
  min-height: 0.125rem;
  flex: 0 0 0.125rem;
  width: 0.95rem;
  min-width: 0.95rem;
  margin: 0;
  padding: 0;
  background: var(--color-background);
  transition: all 0.25s ease-in-out;
  display: block;
  transform-origin: center;
  border-radius: 999px;
  box-sizing: border-box;
}

.hamburger.menu-open span:nth-child(1) {
  transform: rotate(45deg);
  top: 50%;
}

.hamburger.menu-open span:nth-child(2) {
  opacity: 0;
}

.hamburger.menu-open span:nth-child(3) {
  transform: rotate(-45deg);
  top: 50%;
  bottom: auto;
}

.row-menu-panel {
  position: absolute;
  top: calc(100% + 0.35rem);
  right: 0;
  z-index: 20;
  min-width: 11.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.5rem;
  border-radius: 0.9rem;
  border: 0.0625rem solid rgba(var(--vt-c-accent-rgb), 0.4);
  background-color: var(--ui-inverse-surface);
  background-image: none;
  box-shadow:
    0 0 0 1px rgba(var(--vt-c-accent-rgb), 0.25),
    0 1.5rem 2.5rem rgba(10, 12, 20, 0.65),
    inset 0 0 40px rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(1.5rem) saturate(1.2);
  animation: dashboard-pop-in 0.3s cubic-bezier(0.15, 1.8, 0.4, 1);
}

.row-menu.row-menu--drop-up .row-menu-panel {
  top: auto;
  bottom: calc(100% + 0.35rem);
}

.row-menu-item {
  box-sizing: border-box;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 2.5rem;
  padding: 0.6rem 0.85rem;
  margin: 0;
  border: 0;
  border-radius: 0.65rem;
  outline: none;
  box-shadow: none;
  appearance: none;
  background: var(--vt-c-accent);
  color: var(--color-background);
  text-decoration: none;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  font-family: var(--ui-inverse-font-family);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
}

.row-menu-item:hover {
  background-color: var(--ui-inverse-cta-bg-hover);
  color: var(--color-background);
  transform: scale(1.03);
}

.row-menu-item:disabled {
  opacity: 0.55;
  cursor: default;
}

.row-menu-beta-tag {
  position: absolute;
  top: 4px;
  right: 6px;
  padding: 1px 5px;
  background: rgba(7, 42, 32, 0.28);
  color: var(--color-background);
  border-radius: 3px;
  font-size: 0.55em;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  pointer-events: none;
  margin: 0;
}

.action-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  border: 0.0625rem solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: var(--ui-inverse-text-muted);
  text-decoration: none;
  font-size: 0.86rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.action-link:hover {
  border-color: rgba(255, 255, 255, 0.4);
  color: var(--vt-c-white);
}

.action-link-button {
  font: inherit;
}

.empty-state {
  margin-top: 0.5rem;
  padding: 1.25rem;
  text-align: center;
  color: var(--ui-inverse-text-muted);
  background-color: rgba(15, 23, 42, 0.3);
  border-radius: 0.75rem;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  font-size: 0.9rem;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(8, 10, 25, 0.65);
}

.modal-card {
  width: min(32rem, 100%);
  border-radius: 1.1rem;
  border: 0.0625rem solid rgba(var(--vt-c-accent-rgb), 0.3);
  background: var(--ui-inverse-surface);
  box-shadow:
    0 0.75rem 2rem rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(var(--vt-c-accent-rgb), 0.08);
  padding: 1.4rem 1.55rem;
  text-align: left;
}

.modal-card h3 {
  margin: 0 0 0.2rem;
  color: var(--vt-c-white);
  font-size: 1.15rem;
  font-weight: 700;
}

.modal-subtitle {
  margin: 0.35rem 0 1.1rem;
  color: var(--ui-inverse-text-muted);
  font-size: 0.94rem;
  line-height: 1.45;
}

.modal-grid {
  display: grid;
  gap: 1rem;
}

.modal-date-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.modal-field {
  display: grid;
  gap: 0.4rem;
  color: var(--vt-c-white);
  font-size: 0.88rem;
  font-weight: 600;
}

.modal-input {
  border: 0.0625rem solid rgba(var(--vt-c-accent-rgb), 0.25);
  border-radius: 0.65rem;
  background: rgba(0, 0, 0, 0.22);
  color: var(--vt-c-white);
  min-height: 2.4rem;
  padding: 0.5rem 0.7rem;
  font: inherit;
  font-size: 0.9rem;
  transition: border-color 0.15s ease;
}

.modal-input:focus {
  outline: none;
  border-color: rgba(var(--vt-c-accent-rgb), 0.55);
}

.modal-error {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0.75rem 0 0;
  padding: 0.6rem 0.85rem;
  border-radius: 0.6rem;
  background: rgba(255, 80, 80, 0.1);
  border: 1px solid rgba(255, 80, 80, 0.25);
  color: #ffb3b3;
  font-size: 0.85rem;
  line-height: 1.4;
}

.modal-actions {
  margin-top: 1.2rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.65rem;
}

.modal-actions--equal {
  justify-content: stretch;
}

.modal-actions--equal > .primary-button {
  flex: 1;
}

.primary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  min-width: 0;
  background: var(--vt-c-accent);
  color: var(--color-background);
  border: 0.0625rem solid var(--vt-c-accent);
  padding: 0.55rem 0.9rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: var(--font-sans);
  box-shadow: none;
}

.primary-button:hover {
  transform: translateY(-0.0625rem);
  background: var(--ui-inverse-cta-bg-hover);
}

.primary-button:disabled {
  opacity: 0.7;
  cursor: default;
  transform: none;
}

.primary-button-small {
  padding: 0.65rem 1rem;
  font-size: 0.88rem;
}

@keyframes dashboard-pop-in {
  0% {
    opacity: 0;
    transform: translateY(-1rem) scale(0.95) rotate(-2deg);
  }
  40% {
    transform: translateY(0.2rem) scale(1.02) rotate(1deg);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1) rotate(0);
  }
}

.dashboard-updated-at {
  margin: 0.85rem 0 0;
  color: var(--ui-inverse-text-muted);
  font-size: 0.74rem;
  opacity: 0.6;
}

@keyframes dashboard-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (min-width: 640px) {
  .dashboard-container {
    width: 94%;
    padding: 1.5rem;
  }
}

@media (min-width: 1024px) {
  .dashboard-container {
    width: 92%;
    margin-top: 6.25rem;
    padding: 1.5rem;
  }
}

@media (max-width: 640px) {
  .section-header,
  .analytics-entry,
  .filter-toolbar,
  .schedule-line {
    flex-direction: column;
    align-items: flex-start;
  }

  .dashboard-header {
    flex-direction: column;
    align-items: stretch;
  }

  .dashboard-heading {
    width: 100%;
    text-align: center;
  }

  .dashboard-header h2 {
    width: 100%;
    text-align: center;
  }

  .filter-row {
    width: 100%;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .campaign-list-header {
    display: none;
  }

  .campaign-row {
    grid-template-columns: 1fr;
    gap: 0.8rem;
  }

  .campaign-status-column {
    justify-content: flex-start;
  }

  .schedule-value {
    text-align: left;
  }

  .metrics-inline {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 56rem) {
  .dashboard-container:has(.dashboard-menu.open) .dashboard-heading,
  .dashboard-container:has(.dashboard-menu.open) .dashboard-section,
  .dashboard-container:has(.dashboard-menu.open) .dashboard-updated-at,
  .dashboard-container:has(.row-menu.open) .dashboard-header,
  .dashboard-container:has(.row-menu.open) .dashboard-updated-at,
  .dashboard-container:has(.row-menu.open) .campaign-row:not(:has(.row-menu.open)),
  .dashboard-container:has(.row-menu.open) .campaign-row:has(.row-menu.open) > :not(.row-actions) {
    filter: blur(0.28rem) saturate(0.92);
    opacity: 0.74;
    transition: filter 0.18s ease, opacity 0.18s ease;
  }

  .dashboard-container:has(.dashboard-menu.open) .dashboard-menu,
  .dashboard-container:has(.row-menu.open) .campaign-row:has(.row-menu.open) {
    position: relative;
    z-index: 25;
  }

  .dashboard-menu {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    position: relative;
  }

  .dashboard-menu .hamburger {
    align-self: flex-end;
  }

  .dashboard-menu-panel {
    position: absolute;
    top: calc(100% + 0.65rem);
    right: 0;
    left: auto;
    z-index: 30;
    width: min(18rem, calc(100vw - 2rem));
    min-width: 0;
    margin-top: 0.65rem;
    padding: 0.65rem;
    box-sizing: border-box;
    box-shadow:
      0 0 0 1px rgba(150, 255, 201, 0.18),
      0 0.85rem 1.5rem rgba(10, 12, 20, 0.35);
    backdrop-filter: none;
  }

  .dashboard-menu-item {
    min-height: 3rem;
    padding: 0.8rem 1rem;
    font-size: 0.92rem;
    text-align: left;
  }

  .filter-toolbar {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: stretch;
  }

  .filter-row {
    display: contents;
  }

  .filter-chip,
  .refresh-icon-button {
    width: 100%;
    min-width: 0;
  }

  .campaign-list-header {
    display: none;
  }

  .campaign-row {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.75rem;
  }

  .campaign-status-column {
    justify-content: flex-start;
  }

  .row-actions {
    justify-content: flex-start;
    width: 100%;
  }

  .row-menu {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }

  .row-menu .hamburger {
    align-self: flex-end;
  }

  .row-menu-panel {
    position: absolute;
    top: calc(100% + 0.65rem);
    right: 0;
    left: auto;
    width: min(18rem, calc(100vw - 2rem));
    min-width: 0;
    margin-top: 0.65rem;
    padding: 0.65rem;
    box-sizing: border-box;
    box-shadow:
      0 0 0 1px rgba(150, 255, 201, 0.18),
      0 0.85rem 1.5rem rgba(10, 12, 20, 0.35);
    backdrop-filter: none;
  }

  .row-menu.row-menu--drop-up .row-menu-panel {
    top: auto;
    bottom: calc(100% + 0.65rem);
    margin-top: 0;
  }

  .row-menu-item {
    min-height: 3rem;
    justify-content: flex-start;
    padding: 0.8rem 1rem;
    font-size: 0.92rem;
    text-align: left;
  }
}

.usage-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.75rem;
  padding: 0.65rem 0.95rem;
  margin-bottom: 0.85rem;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--vt-c-white, #ffffff);
  font-size: 0.92rem;
}

.usage-banner--at-cap {
  background: rgba(220, 80, 60, 0.12);
  border-color: rgba(220, 80, 60, 0.4);
}

.usage-banner-primary {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
  font-size: 1.05rem;
}

.usage-banner-primary strong {
  font-weight: 700;
}

.usage-banner-of {
  opacity: 0.65;
  font-weight: 500;
}

.usage-banner-meta {
  opacity: 0.7;
  font-size: 0.82rem;
}

.usage-banner-hint {
  margin-left: auto;
  opacity: 0.85;
  font-size: 0.82rem;
  font-style: italic;
}

.swap-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 10, 25, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.swap-modal {
  background: var(--ui-inverse-surface);
  border-radius: 1.1rem;
  border: 0.0625rem solid rgba(var(--vt-c-accent-rgb), 0.25);
  padding: 1.4rem 1.55rem;
  max-width: 480px;
  width: 100%;
  box-shadow:
    0 1.25rem 3.75rem rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(var(--vt-c-accent-rgb), 0.08);
  color: var(--vt-c-white);
}

.swap-modal-title {
  margin: 0 0 0.55rem;
  font-size: 1.15rem;
  font-weight: 700;
}

.swap-modal-body {
  margin: 0 0 1.1rem;
  line-height: 1.45;
  font-size: 0.94rem;
  opacity: 0.88;
}

.swap-modal-empty {
  padding: 0.9rem;
  text-align: center;
  opacity: 0.65;
  font-style: italic;
}

.swap-candidate-list {
  list-style: none;
  margin: 0 0 1.1rem;
  padding: 0;
  max-height: 40vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
}

.swap-candidate {
  display: flex;
  flex-direction: column;
  padding: 0.7rem 0.9rem;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background 120ms ease;
}

.swap-candidate:last-child {
  border-bottom: none;
}

.swap-candidate:hover {
  background: rgba(255, 255, 255, 0.04);
}

.swap-candidate input[type="radio"] {
  accent-color: var(--vt-c-primary, #7a8cff);
  margin-bottom: 0.3rem;
  align-self: flex-start;
}

.swap-candidate-name {
  font-weight: 600;
  font-size: 0.95rem;
}

.swap-candidate-id {
  font-size: 0.72rem;
  opacity: 0.55;
  letter-spacing: 0.02em;
  font-family: 'SF Mono', 'Consolas', monospace;
}

.swap-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
}

/* Over-cap compliance banner */
.overcap-banner {
  margin-bottom: 0.85rem;
  padding: 1rem 1.1rem;
  border-radius: 10px;
  background: rgba(200, 150, 0, 0.1);
  border: 1px solid rgba(200, 150, 0, 0.4);
  color: var(--vt-c-white, #ffffff);
  font-size: 0.93rem;
}

.overcap-banner-header {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-bottom: 0.85rem;
}

.overcap-banner-title {
  font-weight: 700;
  font-size: 1rem;
  color: rgba(255, 200, 50, 0.95);
}

.overcap-banner-body {
  opacity: 0.9;
  line-height: 1.45;
}

.overcap-candidate-list {
  list-style: none;
  margin: 0 0 0.9rem;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  max-height: 40vh;
  overflow-y: auto;
}

.overcap-candidate {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.overcap-candidate:last-child {
  border-bottom: none;
}

.overcap-candidate--selected {
  background: rgba(200, 150, 0, 0.08);
}

.overcap-candidate--done {
  opacity: 0.5;
}

.overcap-candidate-label {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.65rem 0.9rem;
  cursor: pointer;
  transition: background 120ms ease;
}

.overcap-candidate-label:hover {
  background: rgba(255, 255, 255, 0.04);
}

.overcap-candidate-label input[type="checkbox"] {
  accent-color: var(--vt-c-primary, #7a8cff);
  flex-shrink: 0;
  width: 1rem;
  height: 1rem;
}

.overcap-candidate-name {
  font-weight: 600;
  font-size: 0.92rem;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.overcap-candidate-id {
  font-size: 0.7rem;
  opacity: 0.5;
  letter-spacing: 0.02em;
  font-family: 'SF Mono', 'Consolas', monospace;
  flex-shrink: 0;
}

.overcap-candidate-status {
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  flex-shrink: 0;
}

.overcap-candidate-status--ok {
  background: rgba(60, 180, 80, 0.18);
  color: rgba(100, 220, 120, 0.95);
}

.overcap-candidate-status--err {
  background: rgba(220, 60, 60, 0.18);
  color: rgba(255, 100, 100, 0.95);
}

.overcap-error-toast {
  margin-bottom: 0.75rem;
  padding: 0.55rem 0.85rem;
  border-radius: 6px;
  background: rgba(220, 60, 60, 0.15);
  border: 1px solid rgba(220, 60, 60, 0.35);
  color: rgba(255, 140, 140, 0.9);
  font-size: 0.83rem;
}

.overcap-banner-actions {
  display: flex;
  justify-content: flex-end;
}

/* In attach-mode, the CTA is primary — hamburger becomes a quiet secondary affordance */
.row-menu .hamburger {
  opacity: 1;
  transition: opacity 0.15s ease;
}

/* When an attach CTA sibling is present, dim the hamburger */
.row-actions:has(.attach-cta-button) .row-menu .hamburger {
  opacity: 0.55;
  border-color: rgba(var(--vt-c-accent-rgb), 0.4);
  background: rgba(var(--vt-c-accent-rgb), 0.1);
}

.row-actions:has(.attach-cta-button) .row-menu .hamburger span {
  background: rgba(var(--vt-c-accent-rgb), 0.7);
}

.row-actions:has(.attach-cta-button) .row-menu .hamburger:hover {
  opacity: 1;
  background: var(--vt-c-accent);
  border-color: var(--vt-c-accent);
}

.row-actions:has(.attach-cta-button) .row-menu .hamburger:hover span {
  background: var(--color-background);
}

.attach-experience-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  margin-bottom: 0.85rem;
  border-radius: 0.75rem;
  background: rgba(var(--vt-c-accent-rgb), 0.08);
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.3);
  color: var(--vt-c-white);
  font-size: 0.9rem;
  line-height: 1.4;
}

.attach-experience-banner-text {
  flex: 1;
  min-width: 0;
  white-space: normal;
}

.attach-experience-cancel-link {
  flex-shrink: 0;
  width: auto;
  height: auto;
  margin: 0;
  margin-left: auto;
  background: var(--vt-c-accent-light);
  border: 1px solid var(--vt-c-accent);
  border-radius: 999px;
  padding: 0.22rem 0.85rem;
  color: var(--vt-c-background-primary);
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  font-family: inherit;
  box-shadow: none;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  text-decoration: none;
  white-space: nowrap;
}

.attach-experience-cancel-link:hover {
  background: var(--vt-c-accent);
  border-color: var(--vt-c-accent-dark);
  color: var(--vt-c-background-primary);
  transform: none;
  box-shadow: none;
}

.attach-cta-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  padding: 0.5rem 0.9rem;
  border-radius: 999px;
  border: 0.0625rem solid var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: var(--color-background);
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.18s ease, transform 0.15s ease;
  font-family: var(--font-sans);
  white-space: nowrap;
  letter-spacing: 0.01em;
}

.attach-cta-button:hover {
  transform: translateY(-1px);
  background: var(--ui-inverse-cta-bg-hover);
  border-color: var(--ui-inverse-cta-bg-hover);
}

/* ─── Extended modal — Voucher Pool + Pool Allotment sections ─────────── */

.modal-card--extended {
  width: min(40rem, 100%);
  max-height: 90vh;
  overflow-y: auto;
}

.pool-section {
  margin-top: 1.4rem;
  padding-top: 1.2rem;
  border-top: 0.0625rem solid rgba(255, 255, 255, 0.08);
}

.pool-section-header {
  margin-bottom: 0.7rem;
}

.pool-section-title {
  margin: 0;
  color: var(--vt-c-white);
  font-size: 0.96rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.pool-selector-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.8rem;
}

.pool-select {
  flex: 1 1 auto;
}

.pool-field-label {
  display: grid;
  gap: 0.35rem;
  color: var(--vt-c-white);
  font-size: 0.84rem;
  font-weight: 600;
}

.pool-field-optional {
  color: var(--ui-inverse-text-muted);
  font-weight: 400;
}

/* Live counter strip */
.pool-counter-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.5rem;
  padding: 0.55rem 0.7rem;
  border-radius: 0.65rem;
  background: rgba(255, 255, 255, 0.04);
  border: 0.0625rem solid rgba(255, 255, 255, 0.08);
  font-size: 0.82rem;
  margin-bottom: 0.9rem;
}

.pool-counter-item {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
}

.pool-counter-label {
  color: var(--ui-inverse-text-muted);
  font-weight: 500;
}

.pool-counter-value {
  color: var(--vt-c-white);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.pool-counter-sep {
  color: var(--ui-inverse-text-muted);
  opacity: 0.4;
  font-size: 0.75rem;
}

/* Add codes panel */
.pool-add-panel {
  margin-bottom: 0.9rem;
}

.pool-add-tabs {
  display: flex;
  gap: 0.65rem;
  margin-bottom: 0.75rem;
}

.pool-tab {
  flex: 1;
  padding: 0.55rem 0.9rem;
  border: 0.0625rem solid var(--vt-c-accent);
  border-radius: 999px;
  background: var(--vt-c-accent);
  color: var(--color-background);
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}

.pool-add-body {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.pool-generate-form {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.6rem;
  align-items: end;
}

/* File upload */
.pool-file-label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  color: var(--vt-c-white);
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
}

.pool-file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

.pool-file-trigger {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.75rem;
  border-radius: 0.55rem;
  border: 0.0625rem dashed rgba(var(--vt-c-accent-rgb), 0.35);
  background: rgba(255, 255, 255, 0.03);
  color: var(--ui-inverse-text-muted);
  font-size: 0.82rem;
  transition: border-color 0.15s ease, color 0.15s ease;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pool-file-label:hover .pool-file-trigger {
  border-color: rgba(var(--vt-c-accent-rgb), 0.6);
  color: var(--vt-c-white);
}

.pool-csv-preview {
  margin: 0;
  color: var(--ui-inverse-text-muted);
  font-size: 0.8rem;
}

.pool-csv-selected {
  margin: 0;
  color: var(--vt-c-white);
  font-size: 0.82rem;
  word-break: break-all;
}

.pool-add-submit {
  align-self: flex-start;
}

/* Actions row */
.pool-actions-row {
  margin-bottom: 0.7rem;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.65rem;
}

.pool-action-btn {
  flex: 1 1 0;
  min-width: 8rem;
}

.pool-action-btn--danger {
  background: rgba(200, 50, 50, 0.15);
  border-color: rgba(200, 50, 50, 0.5);
  color: rgba(255, 140, 140, 1);
}

.pool-action-btn--danger:hover {
  background: rgba(200, 50, 50, 0.28);
}

.pool-action-item {
  display: flex;
  align-items: center;
}

.pool-danger-link {
  background: none;
  border: none;
  padding: 0;
  color: rgba(255, 100, 100, 0.7);
  font: inherit;
  font-size: 0.82rem;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.15s ease;
}

.pool-danger-link:hover {
  color: rgba(255, 100, 100, 1);
}

.pool-danger-link:disabled {
  opacity: 0.5;
  cursor: default;
}

.pool-confirm-inline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.75rem;
  border-radius: 0.55rem;
  background: rgba(200, 50, 50, 0.08);
  border: 0.0625rem solid rgba(200, 50, 50, 0.2);
}

.pool-confirm-label {
  color: var(--vt-c-white);
  font-size: 0.82rem;
  font-weight: 600;
  flex: 1 1 auto;
}

.pool-danger-confirm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  border: 0.0625rem solid rgba(200, 50, 50, 0.5);
  background: rgba(200, 50, 50, 0.15);
  color: #ffb3b3;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}

.pool-danger-confirm:hover {
  background: rgba(200, 50, 50, 0.28);
}

.pool-danger-confirm:disabled {
  opacity: 0.55;
  cursor: default;
}

/* Code list accordion */
.pool-codes-accordion {
  margin-top: 0.5rem;
}

.pool-codes-toggle {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  width: 100%;
  padding: 0.5rem 0.7rem;
  border: 0.0625rem solid rgba(255, 255, 255, 0.1);
  border-radius: 0.55rem;
  background: rgba(255, 255, 255, 0.03);
  color: var(--ui-inverse-text-muted);
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease, color 0.15s ease;
}

.pool-codes-toggle:hover {
  background: rgba(255, 255, 255, 0.06);
  color: var(--vt-c-white);
}

.pool-codes-toggle-chevron {
  margin-left: auto;
  transition: transform 0.2s ease;
  font-style: normal;
}

.pool-codes-toggle-chevron--open {
  transform: rotate(180deg);
}

.pool-codes-list {
  margin-top: 0.45rem;
  border: 0.0625rem solid rgba(255, 255, 255, 0.08);
  border-radius: 0.55rem;
  max-height: 14rem;
  overflow-y: auto;
}

.pool-code-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.3rem 0.7rem;
  border-bottom: 0.0625rem solid rgba(255, 255, 255, 0.04);
  font-size: 0.8rem;
}

.pool-code-row:last-child {
  border-bottom: none;
}

.pool-code-row--claimed {
  opacity: 0.5;
}

.pool-code-value {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.78rem;
  color: var(--vt-c-white);
}

.pool-code-claimed-badge {
  flex-shrink: 0;
  font-size: 0.78rem;
  opacity: 0.6;
}

.pool-code-remove {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.2rem;
  height: 1.2rem;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  box-shadow: none;
  color: var(--vt-c-white);
  font-size: 0.8rem;
  line-height: 1;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.pool-code-remove:hover {
  opacity: 0.6;
}

.pool-code-remove:disabled {
  opacity: 0.4;
  cursor: default;
}

.pool-codes-loading,
.pool-codes-empty {
  padding: 0.65rem 0.75rem;
  text-align: center;
  color: var(--ui-inverse-text-muted);
  font-size: 0.82rem;
}

.pool-codes-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 0.7rem;
  border-top: 0.0625rem solid rgba(255, 255, 255, 0.06);
  font-size: 0.8rem;
}

.pool-codes-page-info {
  color: var(--ui-inverse-text-muted);
  font-size: 0.78rem;
}

/* Pool allotment section */
.pool-allotment-description {
  margin: 0 0 0.75rem;
  color: var(--ui-inverse-text-muted);
  font-size: 0.84rem;
  line-height: 1.45;
}

.pool-allotment-control {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-bottom: 0.65rem;
}

.pool-allotment-input {
  width: 8rem;
}

.pool-allotment-preview {
  margin: 0;
  color: var(--ui-inverse-text-muted);
  font-size: 0.84rem;
}

.pool-allotment-actions {
  margin-top: 0.55rem;
}

/* Shared utility styles */
.pool-section-error {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  margin: 0.4rem 0;
  padding: 0.5rem 0.7rem;
  border-radius: 0.5rem;
  background: rgba(255, 80, 80, 0.08);
  border: 0.0625rem solid rgba(255, 80, 80, 0.2);
  color: #ffb3b3;
  font-size: 0.8rem;
  line-height: 1.4;
}

.pool-toast {
  margin: 0.35rem 0;
  padding: 0.45rem 0.65rem;
  border-radius: 0.5rem;
  background: rgba(34, 197, 94, 0.08);
  border: 0.0625rem solid rgba(34, 197, 94, 0.2);
  color: #86efac;
  font-size: 0.8rem;
}

.pool-empty-note {
  margin: 0.35rem 0;
  color: var(--ui-inverse-text-muted);
  font-size: 0.84rem;
  font-style: italic;
}

/* ─── Rounds section (sibling to Pool) ──────────────────────────────────── */
.rounds-section {
  margin-top: 0.75rem;
}

.rounds-description {
  margin: 0 0 0.5rem 0;
  color: var(--ui-inverse-text-muted);
  font-size: 0.82rem;
  line-height: 1.4;
}

.round-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.55rem 0;
  border-bottom: 0.0625rem solid rgba(255, 255, 255, 0.06);
}

.round-row:last-of-type {
  border-bottom: none;
}

.round-label {
  flex: 0 0 8rem;
  padding-top: 1.3rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--ui-inverse-text);
}

.round-fields {
  flex: 1 1 auto;
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.round-field {
  flex: 1 1 8rem;
  min-width: 8rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.round-field-attempts {
  flex: 0 0 7rem;
  min-width: 6rem;
}

.round-field > span {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ui-inverse-text-muted);
}

.round-input {
  width: 100%;
}

.round-input-attempts {
  text-align: center;
}

.rounds-actions {
  margin-top: 0.6rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.rounds-library-link {
  font-size: 0.82rem;
  color: var(--accent-primary, #6ad0ff);
  text-decoration: none;
}

.rounds-library-link:hover {
  text-decoration: underline;
}

/* ─── Prize Distribution Section (Section 5) ────────────────────────────── */
.prize-dist-round {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 0.75rem;
  margin-top: 0.5rem;
}

.prize-dist-round:first-child {
  border-top: none;
  padding-top: 0;
  margin-top: 0;
}

.prize-dist-round-label {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ui-inverse-text-muted);
  margin: 0 0 0.5rem;
}
</style>
